import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaCcStripe } from 'react-icons/fa';
import SignaturePad from '../components/SignaturePad';
import { ProductSelection } from '../components/common/ProductSelection';
import SumsubKYC from '../components/kyc/SumsubKYC';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { branding } from '../constants/branding';
import { registrationService } from '../services/registrationService';
import { kycService } from '../services/kycService';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import { emailVerificationService } from '../services/emailVerificationService';
import './EnhancedRegistration.css';

type Address = { street: string; city: string; state: string; postalCode: string; country: string };

type Employment = { employerName: string; jobTitle: string; monthlyIncome: number | ''; duration: string };

type SanctionEntry = { name: string; country: string; from: string };

type Citizenship = { citizenship: string; idPassportNo: string; countryOfIssue: string; expiryDate: string };

type UploadedDocuments = {
  passport?: File[];
  identityCardFront?: File[];
  identityCardBack?: File[];
  utilityBill?: File[];
  bankReference?: File[];
  cv?: File[];
  trustDeed?: File[];
  boardMinutes?: File[];
  powerOfAttorney?: File[];
  other?: File[];
  [key: string]: File[] | undefined;
};

type EnhancedFormData = {
  // Product Information
  productId: number;
  productType: string;
  registrationFee: number;
  registrationFeeType: 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY';
  /** Mirrors product.requiresVerification; false skips document + SUMSUB step. */
  requiresVerification: boolean;

  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  gender: string;
  nationality: string;
  idNumber: string;
  idCountryOfIssue: string;
  idDateOfExpiry: string;
  socialInsuranceNumber: string;
  address: Address;
  employment: Employment;
  pep: boolean;
  sourceOfFunds: string;
  purposeOfAccount: string;
  // Sanctions / Restricted Measures
  underSanctions: boolean;
  sanctionType: string;
  sanctionFrom: string;
  familySanctions: boolean;
  familySanctionsList: SanctionEntry[];
  tradingWithSanctioned: boolean;
  tradingSanctionsList: SanctionEntry[];
  // Citizenship/Tax Residency
  citizenship1: Citizenship;
  citizenship2: Citizenship;
  countryOfTaxation1: string;
  taxId1: string;
  countryOfTaxation2: string;
  taxId2: string;
  nonDomicileCertificate: boolean;
  // Additional KYC fields from PDF
  authorizedPerson: boolean;
  powerOfAttorney: boolean;
  nominee: boolean;
  trustDeed: boolean;
  signatory: boolean;
  boardApproval: boolean;
  // PEP details
  pepDetails: Array<{ position: string; organization: string; country: string; untilWhen: string }>;
  // Family PEP details
  familyPep: boolean;
  familyPepName: string;
  familyPepRelationship: string;
  familyPepDetails: Array<{ position: string; organization: string; country: string; untilWhen: string }>;
  // Close Associate PEP details
  closeAssociatePep: boolean;
  closeAssociatePepName: string;
  closeAssociatePepRelationship: string;
  closeAssociatePepDetails: Array<{ position: string; organization: string; country: string; untilWhen: string }>;
  // Source of Wealth fields
  annualIncomeRange: string;
  earningsAmount: string;
  investmentIncomeAmount: string;
  realEstateIncomeAmount: string;
  allowancesAmount: string;
  otherIncomeAmount: string;
  otherIncomeSpecify: string;
  countryOfOriginIncome: string;
  sourceOfFundsExplanation: string;
  wealthSizeRange: string;
  employmentWealthAmount: string;
  otherInvestmentsAmount: string;
  businessInterestsAmount: string;
  realEstateWealthAmount: string;
  pensionFundsAmount: string;
  inheritanceGiftsAmount: string;
  otherWealthAmount: string;
  otherWealthSpecify: string;
  countryOfOriginWealth: string;
  sourceOfWealthExplanation: string;
  bankruptcyFiled: boolean;
  bankruptcyDetails: string;
  // Occupational/Business Activity fields
  employmentStatus: string;
  occupation: string;
  employerAddress: string;
  employerCountry: string;
  employerDistrict: string;
  employerPosition: string;
  employerName: string;
  dateOfEmployment: string;
  natureOfBusiness: string;
  levelOfEducation: string;
  shareholdingPercentage: string;
  defenceTaxLiable: boolean;
  // Self-Employed fields
  yearsInBusiness: string;
  businessActivityDescription: string;
  nonIncorporatedCompanyName: string;
  selfEmployedShareholding: string;
  companyAddress: string;
  companyPostalCode: string;
  companyTown: string;
  // Previous Occupation fields
  formerEmployerName: string;
  formerNatureOfBusiness: string;
  hireDate: string;
  employmentLocation: string;
  formerOccupation: string;
  formerPosition: string;
  terminationDate: string;
  // Document Verification fields
  documents: {
    passport: boolean;
    identityCard: boolean;
    utilityBill: boolean;
    bankReference: boolean;
    cv: boolean;
    trustDeed: boolean;
    boardMinutes: boolean;
    powerOfAttorney: boolean;
    other: boolean;
    otherSpecify: string;
  };
  documentComments: {
    passport: string;
    identityCard: string;
    utilityBill: string;
    bankReference: string;
    cv: string;
    trustDeed: string;
    boardMinutes: string;
    powerOfAttorney: string;
    other: string;
  };
  uploadedDocuments: UploadedDocuments;
  sumsubVerificationCompleted: boolean;
  // Existing fields
  terms: boolean;
  marketing: boolean;
  signatureDataUrl?: string | null;
};

// Initialize CSS variables from branding
const initializeBrandingStyles = () => {
  const root = document.documentElement;
  
  // Sidebar
  root.style.setProperty('--sidebar-width', branding.sidebar.width);
  root.style.setProperty('--sidebar-background', branding.sidebar.background);
  root.style.setProperty('--sidebar-padding', branding.sidebar.padding);
  root.style.setProperty('--sidebar-text-color', branding.sidebar.textColor);
  
  // Registration Main
  root.style.setProperty('--registration-main-padding', branding.registration.main.padding);
  root.style.setProperty('--registration-main-background', branding.registration.main.background);
  root.style.setProperty('--registration-main-max-width', branding.registration.main.maxWidth);
  
  // Stepper
  root.style.setProperty('--registration-stepper-width', branding.registration.stepper.width);
  root.style.setProperty('--registration-stepper-number-size', branding.registration.stepper.numberSize);
  root.style.setProperty('--registration-stepper-font-size', branding.registration.stepper.fontSize);
  root.style.setProperty('--registration-stepper-line-height', branding.registration.stepper.lineHeight);
  root.style.setProperty('--registration-stepper-active-color', branding.registration.stepper.activeColor);
  root.style.setProperty('--registration-stepper-completed-color', branding.registration.stepper.completedColor);
  root.style.setProperty('--registration-stepper-inactive-color', branding.registration.stepper.inactiveColor);
  root.style.setProperty('--registration-stepper-line-color', branding.registration.stepper.lineColor);
  root.style.setProperty('--registration-stepper-line-thickness', branding.registration.stepper.lineThickness);
  
  // Content
  root.style.setProperty('--registration-content-max-width', branding.registration.content.maxWidth);
  
  // Form Elements
  root.style.setProperty('--form-label-font-size', `${branding.forms.label.fontSize}px`);
  root.style.setProperty('--form-label-font-weight', branding.forms.label.fontWeight.toString());
  root.style.setProperty('--form-label-color', branding.forms.label.color);
  root.style.setProperty('--form-label-margin-bottom', `${branding.forms.label.marginBottom}px`);
  
  root.style.setProperty('--form-input-height', branding.forms.input.height);
  root.style.setProperty('--form-input-font-size', `${branding.forms.input.fontSize}px`);
  root.style.setProperty('--form-input-padding', branding.forms.input.padding);
  root.style.setProperty('--form-input-border-radius', branding.forms.input.borderRadius);
  root.style.setProperty('--form-input-border-color', branding.forms.input.borderColor);
  root.style.setProperty('--form-input-background', branding.forms.input.background);
  root.style.setProperty('--form-input-focus-border-color', branding.forms.input.focusBorderColor);
  root.style.setProperty('--form-input-focus-shadow-color', 'rgba(37, 99, 235, 0.1)');
  root.style.setProperty('--form-input-placeholder-color', branding.forms.input.placeholderColor);
  
  root.style.setProperty('--form-button-height', branding.forms.button.height);
  root.style.setProperty('--form-button-padding', branding.forms.button.padding);
  root.style.setProperty('--form-button-font-size', `${branding.forms.button.fontSize}px`);
  root.style.setProperty('--form-button-font-weight', branding.forms.button.fontWeight.toString());
  root.style.setProperty('--form-button-border-radius', branding.forms.button.borderRadius);
  root.style.setProperty('--form-button-primary-background', branding.forms.button.primary.background);
  root.style.setProperty('--form-button-primary-color', branding.forms.button.primary.color);
  root.style.setProperty('--form-button-primary-hover-background', branding.forms.button.primary.hoverBackground);
  root.style.setProperty('--form-button-secondary-background', branding.forms.button.secondary.background);
  root.style.setProperty('--form-button-secondary-color', branding.forms.button.secondary.color);
  root.style.setProperty('--form-button-secondary-border', branding.forms.button.secondary.border);
  root.style.setProperty('--form-button-secondary-hover-background', branding.forms.button.secondary.hoverBackground);
  root.style.setProperty('--form-button-disabled-opacity', '0.6');
  
  // Content
  root.style.setProperty('--registration-content-subtitle-font-size', `${branding.registration.content.subtitle.fontSize}px`);
  root.style.setProperty('--registration-content-subtitle-color', branding.registration.content.subtitle.color);
  
  // Text colors
  root.style.setProperty('--text-color', branding.textColor);
  root.style.setProperty('--text-secondary', '#6b7280');
  root.style.setProperty('--text-primary', branding.textColor);
  root.style.setProperty('--background', branding.backgroundColor);
  root.style.setProperty('--border', '#e2e8f0');
  
  // Additional required variables
  root.style.setProperty('--primary-color', branding.primaryColor);
  root.style.setProperty('--success', '#059669');
  root.style.setProperty('--error', '#dc2626');
  root.style.setProperty('--surface', '#ffffff');
  root.style.setProperty('--text-light', '#94a3b8');
};

const getUploadedFiles = (documents: UploadedDocuments, key: keyof UploadedDocuments): File[] => {
  return documents[key] || [];
};

const API_BASE_URL = getApiBaseUrl();

const EnhancedPersonalRegister: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<EnhancedFormData>({
    // Product Information defaults
    productId: 0,
    productType: '',
    registrationFee: 0,
    registrationFeeType: 'FIXED_ONE_OFF',
    requiresVerification: true,

    firstName: '', lastName: '', email: '', phoneNumber: '', dateOfBirth: '', gender: '', nationality: '', idNumber: '', idCountryOfIssue: '', idDateOfExpiry: '', socialInsuranceNumber: '',
    address: { street: '', city: '', state: '', postalCode: '', country: '' },
    employment: { employerName: '', jobTitle: '', monthlyIncome: '', duration: '' },
    pep: false,
    sourceOfFunds: '',
    purposeOfAccount: '',
  // Sanctions default values
  underSanctions: false,
  sanctionType: '',
  sanctionFrom: '',
  familySanctions: false,
  familySanctionsList: [{ name: '', country: '', from: '' }, { name: '', country: '', from: '' }, { name: '', country: '', from: '' }],
  tradingWithSanctioned: false,
  tradingSanctionsList: [{ name: '', country: '', from: '' }, { name: '', country: '', from: '' }, { name: '', country: '', from: '' }],
  // Citizenship/Tax Residency defaults
  citizenship1: { citizenship: '', idPassportNo: '', countryOfIssue: '', expiryDate: '' },
  citizenship2: { citizenship: '', idPassportNo: '', countryOfIssue: '', expiryDate: '' },
  countryOfTaxation1: '',
  taxId1: '',
  countryOfTaxation2: '',
  taxId2: '',
  nonDomicileCertificate: false,
  // Additional KYC fields
    authorizedPerson: false,
    powerOfAttorney: false,
    nominee: false,
    trustDeed: false,
    signatory: false,
    boardApproval: false,
    // PEP details
    pepDetails: [{ position: '', organization: '', country: '', untilWhen: '' }, { position: '', organization: '', country: '', untilWhen: '' }],
    // Family PEP details
    familyPep: false,
    familyPepName: '',
    familyPepRelationship: '',
    familyPepDetails: [{ position: '', organization: '', country: '', untilWhen: '' }, { position: '', organization: '', country: '', untilWhen: '' }],
    // Close Associate PEP details
    closeAssociatePep: false,
    closeAssociatePepName: '',
    closeAssociatePepRelationship: '',
    closeAssociatePepDetails: [{ position: '', organization: '', country: '', untilWhen: '' }, { position: '', organization: '', country: '', untilWhen: '' }],
    // Source of Wealth defaults
    annualIncomeRange: '',
    earningsAmount: '',
    investmentIncomeAmount: '',
    realEstateIncomeAmount: '',
    allowancesAmount: '',
    otherIncomeAmount: '',
    otherIncomeSpecify: '',
    countryOfOriginIncome: '',
    sourceOfFundsExplanation: '',
    wealthSizeRange: '',
    employmentWealthAmount: '',
    otherInvestmentsAmount: '',
    businessInterestsAmount: '',
    realEstateWealthAmount: '',
    pensionFundsAmount: '',
    inheritanceGiftsAmount: '',
    otherWealthAmount: '',
    otherWealthSpecify: '',
    countryOfOriginWealth: '',
    sourceOfWealthExplanation: '',
    bankruptcyFiled: false,
    bankruptcyDetails: '',
    // Occupational/Business Activity defaults
    employmentStatus: '',
    occupation: '',
    employerAddress: '',
    employerCountry: '',
    employerDistrict: '',
    employerPosition: '',
    employerName: '',
    dateOfEmployment: '',
    natureOfBusiness: '',
    levelOfEducation: '',
    shareholdingPercentage: '',
    defenceTaxLiable: false,
    // Self-Employed defaults
    yearsInBusiness: '',
    businessActivityDescription: '',
    nonIncorporatedCompanyName: '',
    selfEmployedShareholding: '',
    companyAddress: '',
    companyPostalCode: '',
    companyTown: '',
    // Previous Occupation defaults
    formerEmployerName: '',
    formerNatureOfBusiness: '',
    hireDate: '',
    employmentLocation: '',
    formerOccupation: '',
    formerPosition: '',
    terminationDate: '',
    // Document Verification defaults
    documents: {
      passport: false,
      identityCard: false,
      utilityBill: false,
      bankReference: false,
      cv: false,
      trustDeed: false,
      boardMinutes: false,
      powerOfAttorney: false,
      other: false,
      otherSpecify: '',
    },
    documentComments: {
      passport: '',
      identityCard: '',
      utilityBill: '',
      bankReference: '',
      cv: '',
      trustDeed: '',
      boardMinutes: '',
      powerOfAttorney: '',
      other: '',
    },
    uploadedDocuments: {},
    sumsubVerificationCompleted: false,
    // Existing fields
    terms: false,
    marketing: false,
    signatureDataUrl: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const reviewRef = useRef<HTMLDivElement | null>(null);
  const signaturePadRef = useRef<any>(null);
  
  // KYC state variables
  const [showKycModal, setShowKycModal] = useState(false);
  const [kycApplicantId, setKycApplicantId] = useState<string | null>(null);
  const [kycAccessToken, setKycAccessToken] = useState<string | null>(null);
  const [kycStatus, setKycStatus] = useState<'pending' | 'completed' | 'rejected' | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED'>('PENDING');
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);
  const [emailVerificationMessage, setEmailVerificationMessage] = useState('');

  const verificationRequired = formData.requiresVerification !== false;
  const verificationStepIndex = verificationRequired ? 10 : -1;
  const reviewStepIndex = verificationRequired ? 11 : 10;
  const steps = useMemo(() => {
    const head: string[] = [
      'Product Selection',
      'Personal',
      'Identification',
      'Sanctions / Restricted Measures',
      'Citizenship/Tax Residency',
      'Legal Status',
      'PEP',
      'Occupational/Business Activity',
      'Source of Wealth',
      'Declarations',
    ];
    if (!verificationRequired) {
      return [...head, 'Review & Sign'];
    }
    return [...head, 'Verification of Documents Disclosed', 'Review & Sign'];
  }, [verificationRequired]);

  const reviewStepIndexRef = useRef(11);
  reviewStepIndexRef.current = reviewStepIndex;

  useEffect(() => {
    initializeBrandingStyles();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('payment_result');
    const sessionId = params.get('session_id');
    if (!paymentResult) return;

    const finalize = () => {
      window.history.replaceState({}, document.title, window.location.pathname);
      setActiveStep(reviewStepIndexRef.current);
    };

    if (paymentResult === 'cancelled') {
      setPaymentStatus('EXPIRED');
      setPaymentCompleted(false);
      setError('Payment was cancelled. You can try again.');
      finalize();
      return;
    }

    if (paymentResult === 'success' && sessionId) {
      const checkStatus = async () => {
        try {
          const statusRes = await axios.get(`${API_BASE_URL}/api/public/registration-payments/status/${sessionId}`);
          const status = (statusRes.data?.data?.status || 'PROCESSING') as 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
          setPaymentSessionId(sessionId);
          setPaymentStatus(status);
          setPaymentCompleted(status === 'COMPLETED');
          if (status !== 'COMPLETED') {
            setError('');
            startPaymentStatusPolling(sessionId);
          }
        } catch {
          setError('Unable to verify payment status right now. Please try again.');
        } finally {
          finalize();
        }
      };
      checkStatus();
    }
  }, []);

  useEffect(() => {
    setEmailVerified(false);
    setEmailVerificationSent(false);
    setEmailVerificationMessage('');
  }, [formData.email]);

  const sendVerificationLink = async () => {
    if (!formData.email) {
      setEmailVerificationMessage('Enter an email first.');
      return;
    }
    try {
      setEmailVerificationLoading(true);
      setEmailVerificationMessage('');
      const result = await emailVerificationService.sendLink(formData.email);
      setEmailVerificationSent(true);
      setEmailVerificationMessage(result.message || 'Verification link sent.');
    } catch (err: any) {
      setEmailVerificationMessage(
        err?.response?.data?.errors?.[0]
          || err?.response?.data?.message
          || 'Failed to send verification link.'
      );
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  const refreshEmailVerificationStatus = async () => {
    if (!formData.email) {
      setEmailVerificationMessage('Enter an email first.');
      return;
    }
    try {
      setEmailVerificationLoading(true);
      const status = await emailVerificationService.getStatus(formData.email);
      setEmailVerified(Boolean(status.verified));
      setEmailVerificationMessage(status.verified
        ? 'Email verified. You can continue.'
        : 'Email not verified yet. Click the verification link and check again.'
      );
    } catch {
      setEmailVerificationMessage('Failed to check verification status.');
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  const isStepValid = useMemo(() => {
    if (activeStep === reviewStepIndex) {
      return !!formData.signatureDataUrl;
    }
    if (activeStep === verificationStepIndex) {
      const hasDocuments = Object.values(formData.documents).some(selected => selected);
      return hasDocuments && formData.sumsubVerificationCompleted;
    }
    switch (activeStep) {
      case 0:
        return !!(formData.productId && formData.productId > 0);
      case 1:
        return !!(formData.firstName && formData.lastName && formData.email && formData.phoneNumber && formData.dateOfBirth && formData.gender && formData.nationality && emailVerified);
      case 2:
        return !!(formData.idNumber && formData.idCountryOfIssue && formData.idDateOfExpiry && formData.socialInsuranceNumber);
      case 3:
        // Sanctions step: no required fields, but structure present
        return true;
      case 4:
        // Citizenship/Tax Residency step - at least citizenship1 is required
        return !!(formData.citizenship1.citizenship && formData.citizenship1.idPassportNo && formData.citizenship1.countryOfIssue);
      case 5:
        // Legal Status step - all fields are optional but user should at least answer the main questions
        return true; // This step is informational, no required fields
      case 6:
        // PEP step - no required fields, but structure present
        return true;
      case 7:
        // Occupational/Business Activity step: require employment status selection
        return !!formData.employmentStatus;
      case 8:
        // Source of Wealth step: require wealth size range and at least one source of wealth amount
        return !!(formData.wealthSizeRange && (formData.employmentWealthAmount || formData.otherInvestmentsAmount || formData.businessInterestsAmount || formData.realEstateWealthAmount || formData.pensionFundsAmount || formData.inheritanceGiftsAmount || formData.otherWealthAmount));
      case 9:
        // Declarations step: require terms acceptance
        return !!formData.terms;
      default:
        return false;
    }
  }, [activeStep, formData, reviewStepIndex, verificationStepIndex]);

  const handleNext = async () => {
    if (!isStepValid) return;
    if (activeStep < steps.length - 1) {
      setActiveStep(s => s + 1);
      return;
    }
    if (formData.registrationFeeType !== 'NO_FEE' && !paymentCompleted) {
      setError('Please complete the registration payment before continuing.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // First generate and download PDF, and keep a copy to upload after submit
      const pdfFile = await handleDownloadPdf();
      
      // Then submit the registration data
      // Extract files and prepare form data
      const { uploadedDocuments, ...formDataWithoutFiles } = formData;
      
      // Extract files with proper type definition
      interface DocumentFiles {
        utilityBill?: File;
        passport?: File;
        idFront?: File;
        idBack?: File;
        cv?: File;
        bankReference?: File;
        powerOfAttorney?: File;
        trustDeed?: File;
        boardMinutes?: File;
      }

      const files: DocumentFiles = {
        utilityBill: uploadedDocuments?.utilityBill?.[0],
        passport: uploadedDocuments?.passport?.[0],
        idFront: uploadedDocuments?.identityCardFront?.[0],
        idBack: uploadedDocuments?.identityCardBack?.[0],
        cv: uploadedDocuments?.cv?.[0],
        bankReference: uploadedDocuments?.bankReference?.[0],
        powerOfAttorney: uploadedDocuments?.powerOfAttorney?.[0],
        trustDeed: uploadedDocuments?.trustDeed?.[0],
        boardMinutes: uploadedDocuments?.boardMinutes?.[0]
      };

      // Clean up documents object to match backend DTO
      formDataWithoutFiles.documents = {
        passport: !!uploadedDocuments?.passport?.length,
        identityCard: !!(uploadedDocuments?.identityCardFront?.length && uploadedDocuments?.identityCardBack?.length),
        utilityBill: !!uploadedDocuments?.utilityBill?.length,
        bankReference: !!uploadedDocuments?.bankReference?.length,
        cv: !!uploadedDocuments?.cv?.length,
        trustDeed: !!uploadedDocuments?.trustDeed?.length,
        boardMinutes: !!uploadedDocuments?.boardMinutes?.length,
        powerOfAttorney: !!uploadedDocuments?.powerOfAttorney?.length,
        other: !!uploadedDocuments?.other?.length,
        otherSpecify: formDataWithoutFiles.documents.otherSpecify || ''
      };

      // Create FormData instance
      const formDataToSend = new FormData();

      // Add files if they exist
      if (files.utilityBill) formDataToSend.append('utilityBill', files.utilityBill);
      if (files.passport) formDataToSend.append('passport', files.passport);
      if (files.idFront) formDataToSend.append('idFront', files.idFront);
      if (files.idBack) formDataToSend.append('idBack', files.idBack);
      
      // Log what we're sending
      console.log('Files being sent:', files);
      console.log('Form data being sent:', formDataWithoutFiles);

      // Create FormData instance with all data and files
      const formDataForSubmission = {
        ...formDataWithoutFiles,
        // Convert monthly income to string and handle signatureDataUrl
        employment: {
          ...formDataWithoutFiles.employment,
          monthlyIncome: formDataWithoutFiles.employment.monthlyIncome.toString()
        },
        signatureDataUrl: formDataWithoutFiles.signatureDataUrl || undefined,
        // Use the document flags from the form data but combine front/back into identityCard
        documents: {
          passport: !!uploadedDocuments?.passport?.length,
          identityCard: !!(uploadedDocuments?.identityCardFront?.length || uploadedDocuments?.identityCardBack?.length),
          utilityBill: !!uploadedDocuments?.utilityBill?.length,
          bankReference: !!uploadedDocuments?.bankReference?.length,
          cv: !!uploadedDocuments?.cv?.length,
          trustDeed: !!uploadedDocuments?.trustDeed?.length,
          boardMinutes: !!uploadedDocuments?.boardMinutes?.length,
          powerOfAttorney: !!uploadedDocuments?.powerOfAttorney?.length,
          other: !!uploadedDocuments?.other?.length,
          otherSpecify: formDataWithoutFiles.documents.otherSpecify || ''
        }
      };

      // Add form data as JSON string
      formDataToSend.append('formData', JSON.stringify(formDataForSubmission));

      // Add files if they exist
      if (files.utilityBill) formDataToSend.append('utilityBill', files.utilityBill);
      if (files.passport) formDataToSend.append('passport', files.passport);
      if (files.idFront) formDataToSend.append('idFront', files.idFront);
      if (files.idBack) formDataToSend.append('idBack', files.idBack);
      if (files.cv) formDataToSend.append('cv', files.cv);
      if (files.bankReference) formDataToSend.append('bankReference', files.bankReference);
      if (files.powerOfAttorney) formDataToSend.append('powerOfAttorney', files.powerOfAttorney);
      if (files.trustDeed) formDataToSend.append('trustDeed', files.trustDeed);
      if (files.boardMinutes) formDataToSend.append('boardMinutes', files.boardMinutes);

      // Send the FormData object containing both form data and files
      const response = await registrationService.submitEnhancedPersonalRegistration(formDataToSend);

      if (response.success) {
        // Upload the generated PDF after successful submission
        try {
          if (pdfFile) {
            await registrationService.uploadEnhancedPersonalSummaryPdf(formData.email, pdfFile);
          }
        } catch (uploadErr) {
          console.error('Failed to upload generated PDF after submission:', uploadErr);
        }
        // Navigate to login page after successful registration
        navigate('/login', {
          state: {
            message: 'Personal registration submitted successfully! Our compliance team will review your application. Please login to continue.',
            type: 'success'
          }
        });
      } else {
        setError(response.error || 'Failed to submit registration');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationPayment = () => {
    setError('');
    if (!formData.productId || Number(formData.registrationFee || 0) <= 0) {
      setError('Please select a tier with a valid registration fee before starting payment.');
      return;
    }
    const startPayment = async () => {
      const response = await axios.post(`${API_BASE_URL}/api/public/registration-payments/create-session`, {
        registrationType: 'PERSONAL',
        email: formData.email,
        productId: formData.productId,
        currency: 'EUR'
      });
      if (!response.data?.success || !response.data?.data?.checkoutUrl || !response.data?.data?.sessionId) {
        setError(response.data?.error || 'Failed to create payment session.');
        return;
      }
      const sessionId = response.data.data.sessionId as string;
      setPaymentSessionId(sessionId);
      setPaymentStatus('PROCESSING');
      window.open(response.data.data.checkoutUrl, '_blank', 'noopener,noreferrer');
      startPaymentStatusPolling(sessionId);
    };
    startPayment().catch((err: any) => setError(err?.response?.data?.error || err?.message || 'Failed to start payment.'));
  };

  const startPaymentStatusPolling = (sessionId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const statusRes = await axios.get(`${API_BASE_URL}/api/public/registration-payments/status/${sessionId}`);
        const status = (statusRes.data?.data?.status || 'PROCESSING') as 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
        setPaymentStatus(status);
        if (status === 'COMPLETED') {
          setPaymentCompleted(true);
          clearInterval(interval);
        }
        if (status === 'FAILED' || status === 'EXPIRED') {
          setPaymentCompleted(false);
          clearInterval(interval);
        }
      } catch {
        // keep polling
      }
      if (attempts >= maxAttempts) clearInterval(interval);
    }, 5000);
  };

  const handleBack = () => setActiveStep(s => Math.max(0, s - 1));

  const handleChange = (path: string, value: any) => {
    setFormData(prev => {
      const clone: any = { ...prev };
      const parts = path.split('.');
      let cursor = clone;
      for (let i = 0; i < parts.length - 1; i++) cursor = cursor[parts[i]];
      cursor[parts[parts.length - 1]] = value;
      return clone as EnhancedFormData;
    });
  };

  const fillWithMockData = () => {
    setFormData({
      // Product Information mock data
      productId: 1,
      productType: 'PERSONAL',
      registrationFee: 25,
      registrationFeeType: 'FIXED_ONE_OFF',
      requiresVerification: true,

      firstName: 'John',
      lastName: 'Smith',
      email: 'john.smith@example.com',
      phoneNumber: '+1-555-123-4567',
      dateOfBirth: '1985-06-15',
      gender: 'Male',
      nationality: 'American',
      idNumber: 'P123456789',
      idCountryOfIssue: 'United States',
      idDateOfExpiry: '2030-12-31',
      socialInsuranceNumber: '123-45-6789',
      address: { 
        street: '123 Main Street', 
        city: 'New York', 
        state: 'NY', 
        postalCode: '10001', 
        country: 'United States' 
      },
      employment: { 
        employerName: 'Tech Solutions Inc', 
        jobTitle: 'Software Engineer', 
        monthlyIncome: 7500, 
        duration: '3 years' 
      },
      pep: false,
      sourceOfFunds: 'Employment income',
      purposeOfAccount: 'Personal banking and investment',
      // Source of Wealth mock data
      annualIncomeRange: '€100.001 - €500.000',
      earningsAmount: '75000',
      investmentIncomeAmount: '15000',
      realEstateIncomeAmount: '12000',
      allowancesAmount: '5000',
      otherIncomeAmount: '3000',
      otherIncomeSpecify: 'retirement plan',
      countryOfOriginIncome: 'United States, Canada',
      sourceOfFundsExplanation: 'Primary income from employment as Software Engineer at Tech Solutions Inc. Additional income from investment portfolio managed by Fidelity Investments and rental income from property in New York.',
      wealthSizeRange: '€100.001 - €500.000',
      employmentWealthAmount: '200000',
      otherInvestmentsAmount: '50000',
      businessInterestsAmount: '25000',
      realEstateWealthAmount: '150000',
      pensionFundsAmount: '75000',
      inheritanceGiftsAmount: '0',
      otherWealthAmount: '0',
      otherWealthSpecify: '',
      countryOfOriginWealth: 'United States, Canada',
      sourceOfWealthExplanation: 'Wealth accumulated through 10+ years of employment, prudent investment in diversified portfolio, and real estate appreciation. No inheritance or gifts received.',
      bankruptcyFiled: false,
      bankruptcyDetails: '',
      // Occupational/Business Activity mock data
      employmentStatus: 'Employed',
      occupation: 'Software Engineering',
      employerAddress: '456 Business Street, Suite 200',
      employerCountry: 'United States',
      employerDistrict: 'Manhattan',
      employerPosition: 'Senior Software Engineer',
      employerName: 'Tech Solutions Inc.',
      dateOfEmployment: '2020-03-15',
      natureOfBusiness: 'Technology and Software Development',
      levelOfEducation: 'Bachelor\'s Degree',
      shareholdingPercentage: '0',
      defenceTaxLiable: false,
      // Self-Employed mock data
      yearsInBusiness: '3',
      businessActivityDescription: 'Freelance software development and consulting services for small to medium businesses',
      nonIncorporatedCompanyName: 'John Smith Consulting',
      selfEmployedShareholding: '100',
      companyAddress: '123 Main Street',
      companyPostalCode: '10001',
      companyTown: 'New York',
      // Previous Occupation mock data
      formerEmployerName: 'Previous Tech Corp',
      formerNatureOfBusiness: 'Software Development',
      hireDate: '2018-01-15',
      employmentLocation: 'United States',
      formerOccupation: 'Software Developer',
      formerPosition: 'Junior Developer',
      terminationDate: '2020-02-28',
      // Sanctions
      underSanctions: false,
      sanctionType: '',
      sanctionFrom: '',
      familySanctions: false,
      familySanctionsList: [
        { name: '', country: '', from: '' },
        { name: '', country: '', from: '' },
        { name: '', country: '', from: '' }
      ],
      tradingWithSanctioned: false,
      tradingSanctionsList: [
        { name: '', country: '', from: '' },
        { name: '', country: '', from: '' },
        { name: '', country: '', from: '' }
      ],
      // Citizenship/Tax Residency
      citizenship1: { 
        citizenship: 'American', 
        idPassportNo: 'P123456789', 
        countryOfIssue: 'United States', 
        expiryDate: '2030-12-31' 
      },
      citizenship2: { 
        citizenship: 'Canadian', 
        idPassportNo: 'C987654321', 
        countryOfIssue: 'Canada', 
        expiryDate: '2029-06-15' 
      },
      countryOfTaxation1: 'United States',
      taxId1: '123-45-6789',
      countryOfTaxation2: 'Canada',
      taxId2: '987-65-4321',
      nonDomicileCertificate: false,
      // Legal Status
      authorizedPerson: false,
      powerOfAttorney: false,
      nominee: false,
      trustDeed: false,
      signatory: true,
      boardApproval: true,
      // PEP details
      pepDetails: [{ position: 'Minister of Finance', organization: 'Government of Cyprus', country: 'Cyprus', untilWhen: '2020-12-31' }, { position: 'Board Member', organization: 'Central Bank', country: 'Cyprus', untilWhen: '2018-06-30' }],
      // Family PEP details
      familyPep: true,
      familyPepName: 'John Smith Sr.',
      familyPepRelationship: 'Father',
      familyPepDetails: [{ position: 'Mayor', organization: 'City Council', country: 'Cyprus', untilWhen: '2019-12-31' }, { position: '', organization: '', country: '', untilWhen: '' }],
      // Close Associate PEP details
      closeAssociatePep: false,
      closeAssociatePepName: '',
      closeAssociatePepRelationship: '',
      closeAssociatePepDetails: [{ position: '', organization: '', country: '', untilWhen: '' }, { position: '', organization: '', country: '', untilWhen: '' }],
      // Document Verification mock data
      documents: {
        passport: true,
        identityCard: false,
        utilityBill: true,
        bankReference: false,
        cv: false,
        trustDeed: false,
        boardMinutes: false,
        powerOfAttorney: false,
        other: false,
        otherSpecify: '',
      },
      documentComments: {
        passport: 'Valid US passport',
        identityCard: '',
        utilityBill: 'Electricity bill from last month',
        bankReference: '',
        cv: '',
        trustDeed: '',
        boardMinutes: '',
        powerOfAttorney: '',
        other: '',
      },
      uploadedDocuments: {},
      sumsubVerificationCompleted: true,
      // Existing fields
      terms: true,
      marketing: false,
      signatureDataUrl: null
    });
  };

  const clearAllData = () => {
    setFormData({
      // Product Information defaults
      productId: 0,
      productType: '',
      registrationFee: 0,
      registrationFeeType: 'FIXED_ONE_OFF',
      requiresVerification: true,

      firstName: '', lastName: '', email: '', phoneNumber: '', dateOfBirth: '', gender: '', nationality: '', idNumber: '', idCountryOfIssue: '', idDateOfExpiry: '', socialInsuranceNumber: '',
      address: { street: '', city: '', state: '', postalCode: '', country: '' },
      employment: { employerName: '', jobTitle: '', monthlyIncome: '', duration: '' },
      pep: false,
      sourceOfFunds: '',
      purposeOfAccount: '',
      // Source of Wealth defaults
      annualIncomeRange: '',
      earningsAmount: '',
      investmentIncomeAmount: '',
      realEstateIncomeAmount: '',
      allowancesAmount: '',
      otherIncomeAmount: '',
      otherIncomeSpecify: '',
      countryOfOriginIncome: '',
      sourceOfFundsExplanation: '',
      wealthSizeRange: '',
      employmentWealthAmount: '',
      otherInvestmentsAmount: '',
      businessInterestsAmount: '',
      realEstateWealthAmount: '',
      pensionFundsAmount: '',
      inheritanceGiftsAmount: '',
      otherWealthAmount: '',
      otherWealthSpecify: '',
      countryOfOriginWealth: '',
      sourceOfWealthExplanation: '',
      bankruptcyFiled: false,
      bankruptcyDetails: '',
      // Sanctions
      underSanctions: false,
      sanctionType: '',
      sanctionFrom: '',
      familySanctions: false,
      familySanctionsList: [{ name: '', country: '', from: '' }, { name: '', country: '', from: '' }, { name: '', country: '', from: '' }],
      tradingWithSanctioned: false,
      tradingSanctionsList: [{ name: '', country: '', from: '' }, { name: '', country: '', from: '' }, { name: '', country: '', from: '' }],
      // Citizenship/Tax Residency defaults
      citizenship1: { citizenship: '', idPassportNo: '', countryOfIssue: '', expiryDate: '' },
      citizenship2: { citizenship: '', idPassportNo: '', countryOfIssue: '', expiryDate: '' },
      countryOfTaxation1: '',
      taxId1: '',
      countryOfTaxation2: '',
      taxId2: '',
      nonDomicileCertificate: false,
      // Additional KYC fields
      authorizedPerson: false,
      powerOfAttorney: false,
      nominee: false,
      trustDeed: false,
      signatory: false,
      boardApproval: false,
      // PEP details
      pepDetails: [{ position: '', organization: '', country: '', untilWhen: '' }, { position: '', organization: '', country: '', untilWhen: '' }],
      // Family PEP details
      familyPep: false,
      familyPepName: '',
      familyPepRelationship: '',
      familyPepDetails: [{ position: '', organization: '', country: '', untilWhen: '' }, { position: '', organization: '', country: '', untilWhen: '' }],
      // Close Associate PEP details
      closeAssociatePep: false,
      closeAssociatePepName: '',
      closeAssociatePepRelationship: '',
      closeAssociatePepDetails: [{ position: '', organization: '', country: '', untilWhen: '' }, { position: '', organization: '', country: '', untilWhen: '' }],
      // Occupational/Business Activity defaults
      employmentStatus: '',
      occupation: '',
      employerAddress: '',
      employerCountry: '',
      employerDistrict: '',
      employerPosition: '',
      employerName: '',
      dateOfEmployment: '',
      natureOfBusiness: '',
      levelOfEducation: '',
      shareholdingPercentage: '',
      defenceTaxLiable: false,
      // Self-Employed defaults
      yearsInBusiness: '',
      businessActivityDescription: '',
      nonIncorporatedCompanyName: '',
      selfEmployedShareholding: '',
      companyAddress: '',
      companyPostalCode: '',
      companyTown: '',
      // Previous Occupation defaults
      formerEmployerName: '',
      formerNatureOfBusiness: '',
      hireDate: '',
      employmentLocation: '',
      formerOccupation: '',
      formerPosition: '',
      terminationDate: '',
      // Document Verification defaults
      documents: {
        passport: false,
        identityCard: false,
        utilityBill: false,
        bankReference: false,
        cv: false,
        trustDeed: false,
        boardMinutes: false,
        powerOfAttorney: false,
        other: false,
        otherSpecify: '',
      },
      documentComments: {
        passport: '',
        identityCard: '',
        utilityBill: '',
        bankReference: '',
        cv: '',
        trustDeed: '',
        boardMinutes: '',
        powerOfAttorney: '',
        other: '',
      },
      uploadedDocuments: {},
      sumsubVerificationCompleted: false,
      // Existing fields
      terms: false,
      marketing: false,
      signatureDataUrl: null
    });
  };

  const handleDownloadPdf = async (): Promise<File | null> => {
    try {
      setLoading(true);
      setError('');

      const doc = new jsPDF('p', 'mm', 'a4');
      const margin = 15;
      let y = 20;

      const addH = (title: string) => {
        y += 8; // Add extra spacing before section title
        if (y > 270) { 
          doc.addPage(); 
          y = 20; 
        }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(14);
        doc.text(title, margin, y); y += 8;
        doc.setDrawColor(0,0,0); doc.line(margin, y, 195, y); y += 6;
      };
      const addKV = (k: string, v: string) => {
        // Calculate dynamic key width based on key length
        const keyText = `${k}:`;
        const keyWidth = Math.max(50, doc.getTextWidth(keyText) + 5); // Minimum 50mm, or text width + 5mm
        const valueStartX = margin + keyWidth;
        const maxWidth = 195 - valueStartX; // Available width for values
        
        // Add key
        doc.setFont('helvetica', 'bold'); 
        doc.setFontSize(10); 
        doc.text(keyText, margin, y);
        
        // Add value with text wrapping
        doc.setFont('helvetica', 'normal'); 
        const value = v || '-';
        
        // Split long text into multiple lines
        const lines = doc.splitTextToSize(value, maxWidth);
        const lineHeight = 5;
        
        // Draw each line
        lines.forEach((line: string, index: number) => {
          if (y > 270) { 
            doc.addPage(); 
            y = 20; 
          }
          doc.text(line, valueStartX, y);
          if (index < lines.length - 1) {
            y += lineHeight;
          }
        });
        
        y += lineHeight + 1; // Add spacing after the field
        if (y > 270) { 
          doc.addPage(); 
          y = 20; 
        }
      };

      // Title
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
      doc.text('KYC Form for Individuals', margin, y); y += 10;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y); y += 10;

      addH('1. Selected Product');
      addKV('Product ID', formData.productId.toString());
      addKV('Product Type', formData.productType);

      addH('2. Personal Details');
      addKV('First Name', formData.firstName);
      addKV('Last Name', formData.lastName);
      addKV('Email', formData.email);
      addKV('Phone', formData.phoneNumber);
      addKV('Date of Birth', formData.dateOfBirth);
      addKV('Gender', formData.gender);
      addKV('Nationality', formData.nationality);

      addH('3. Identification');
      addKV('ID/Passport Number', formData.idNumber);
      addKV('Country of Issue', formData.idCountryOfIssue);
      addKV('Date of Expiry', formData.idDateOfExpiry);
      addKV('Social Insurance Number', formData.socialInsuranceNumber);

      addH('4. Address');
      addKV('Street', formData.address.street);
      addKV('City', formData.address.city);
      addKV('State', formData.address.state);
      addKV('Postal Code', formData.address.postalCode);
      addKV('Country', formData.address.country);

      addH('5. Occupational/Business Activity');
      addKV('Employment Status', formData.employmentStatus || 'Not specified');
      
      if (formData.employmentStatus === 'Employed') {
        addKV('Occupation', formData.occupation);
        addKV('Position', formData.employerPosition);
        addKV('Date of Employment', formData.dateOfEmployment);
        addKV('Nature of Business', formData.natureOfBusiness);
        addKV('Employer Name', formData.employerName);
        addKV('Level of Education', formData.levelOfEducation);
        addKV('Shareholding Percentage', formData.shareholdingPercentage + '%');
        addKV('Defence Tax Liable', formData.defenceTaxLiable ? 'Yes' : 'No');
        addKV('Employer Address', `${formData.employerAddress}, ${formData.employerDistrict}, ${formData.employerCountry}`);
      }
      
      if (formData.employmentStatus === 'Self-Employed') {
        addKV('Years in Business', formData.yearsInBusiness);
        addKV('Business Activity Description', formData.businessActivityDescription);
        addKV('Non-Incorporated Company Name', formData.nonIncorporatedCompanyName);
        addKV('Shareholding Percentage', formData.selfEmployedShareholding + '%');
        addKV('Company Address', `${formData.companyAddress}, ${formData.companyTown}, ${formData.companyPostalCode}`);
      }
      
      if (formData.employmentStatus === 'Retired' || formData.employmentStatus === 'Unemployed' || formData.employmentStatus === 'Other') {
        addKV('Former Employer Name', formData.formerEmployerName);
        addKV('Nature of Business', formData.formerNatureOfBusiness);
        addKV('Hire Date', formData.hireDate);
        addKV('Termination Date', formData.terminationDate);
        addKV('Employment Location', formData.employmentLocation);
        addKV('Former Occupation', formData.formerOccupation);
        addKV('Former Position', formData.formerPosition);
      }

      addH('6. PEP (Politically Exposed Person)');
      addKV('Personal PEP Status', formData.pep ? 'Yes' : 'No');
      if (formData.pep && formData.pepDetails && formData.pepDetails.some(detail => detail.position || detail.organization || detail.country || detail.untilWhen)) {
        (formData.pepDetails || []).forEach((detail, i) => {
          if (detail.position || detail.organization || detail.country || detail.untilWhen) {
            addKV(`Personal PEP Position ${i + 1}`, `${detail.position} at ${detail.organization}, ${detail.country} (until ${detail.untilWhen || 'N/A'})`);
          }
        });
      }
      
      addKV('Family PEP Status', formData.familyPep ? 'Yes' : 'No');
      if (formData.familyPep) {
        addKV('Family PEP Name', formData.familyPepName);
        addKV('Family PEP Relationship', formData.familyPepRelationship);
        if (formData.familyPepDetails && formData.familyPepDetails.some(detail => detail.position || detail.organization || detail.country || detail.untilWhen)) {
          (formData.familyPepDetails || []).forEach((detail, i) => {
            if (detail.position || detail.organization || detail.country || detail.untilWhen) {
              addKV(`Family PEP Position ${i + 1}`, `${detail.position} at ${detail.organization}, ${detail.country} (until ${detail.untilWhen || 'N/A'})`);
            }
          });
        }
      }
      
      addKV('Close Associate PEP Status', formData.closeAssociatePep ? 'Yes' : 'No');
      if (formData.closeAssociatePep) {
        addKV('Close Associate PEP Name', formData.closeAssociatePepName);
        addKV('Close Associate PEP Relationship', formData.closeAssociatePepRelationship);
        if (formData.closeAssociatePepDetails && formData.closeAssociatePepDetails.some(detail => detail.position || detail.organization || detail.country || detail.untilWhen)) {
          (formData.closeAssociatePepDetails || []).forEach((detail, i) => {
            if (detail.position || detail.organization || detail.country || detail.untilWhen) {
              addKV(`Close Associate PEP Position ${i + 1}`, `${detail.position} at ${detail.organization}, ${detail.country} (until ${detail.untilWhen || 'N/A'})`);
            }
          });
        }
      }

      addH('7. Source of Wealth');
      addKV('Annual Income Range', formData.annualIncomeRange);
      addKV('Earnings Amount', formData.earningsAmount ? `€${formData.earningsAmount}` : 'N/A');
      addKV('Investment Income Amount', formData.investmentIncomeAmount ? `€${formData.investmentIncomeAmount}` : 'N/A');
      addKV('Real Estate Income Amount', formData.realEstateIncomeAmount ? `€${formData.realEstateIncomeAmount}` : 'N/A');
      addKV('Allowances Amount', formData.allowancesAmount ? `€${formData.allowancesAmount}` : 'N/A');
      addKV('Other Income Amount', formData.otherIncomeAmount ? `€${formData.otherIncomeAmount}` : 'N/A');
      if (formData.otherIncomeSpecify) addKV('Other Income Specify', formData.otherIncomeSpecify);
      addKV('Country of Origin Income', formData.countryOfOriginIncome);
      if (formData.sourceOfFundsExplanation) addKV('Source of Funds Explanation', formData.sourceOfFundsExplanation);
      
      addKV('Wealth Size Range', formData.wealthSizeRange);
      addKV('Employment Wealth Amount', formData.employmentWealthAmount ? `€${formData.employmentWealthAmount}` : 'N/A');
      addKV('Other Investments Amount', formData.otherInvestmentsAmount ? `€${formData.otherInvestmentsAmount}` : 'N/A');
      addKV('Business Interests Amount', formData.businessInterestsAmount ? `€${formData.businessInterestsAmount}` : 'N/A');
      addKV('Real Estate Wealth Amount', formData.realEstateWealthAmount ? `€${formData.realEstateWealthAmount}` : 'N/A');
      addKV('Pension Funds Amount', formData.pensionFundsAmount ? `€${formData.pensionFundsAmount}` : 'N/A');
      addKV('Inheritance/Gifts Amount', formData.inheritanceGiftsAmount ? `€${formData.inheritanceGiftsAmount}` : 'N/A');
      addKV('Other Wealth Amount', formData.otherWealthAmount ? `€${formData.otherWealthAmount}` : 'N/A');
      if (formData.otherWealthSpecify) addKV('Other Wealth Specify', formData.otherWealthSpecify);
      addKV('Country of Origin Wealth', formData.countryOfOriginWealth);
      if (formData.sourceOfWealthExplanation) addKV('Source of Wealth Explanation', formData.sourceOfWealthExplanation);
      
      addKV('Bankruptcy Filed', formData.bankruptcyFiled ? 'Yes' : 'No');
      if (formData.bankruptcyDetails) addKV('Bankruptcy Details', formData.bankruptcyDetails);

      addH('8. Declarations');
      addKV('Accepted Terms', formData.terms ? 'Yes' : 'No');
      addKV('Marketing Opt-in', formData.marketing ? 'Yes' : 'No');

      addH('9. Sanctions / Restricted Measures');
      addKV('Are you under sanctions?', formData.underSanctions ? 'Yes' : 'No');
      if (formData.underSanctions) {
        addKV('Type of sanctions', formData.sanctionType);
        addKV('From when', formData.sanctionFrom);
      }
      addKV('Family/associates under sanctions', formData.familySanctions ? 'Yes' : 'No');
      if (formData.familySanctions) {
        (formData.familySanctionsList || []).forEach((e, i) => {
          if (e.name || e.country || e.from) {
            addKV(`Family/Associate ${i + 1}`, `${e.name} | ${e.country} | ${e.from}`);
          }
        });
      }
      addKV('Trading with sanctioned entities', formData.tradingWithSanctioned ? 'Yes' : 'No');
      if (formData.tradingWithSanctioned) {
        (formData.tradingSanctionsList || []).forEach((e, i) => {
          if (e.name || e.country || e.from) {
            addKV(`Counterparty ${i + 1}`, `${e.name} | ${e.country} | ${e.from}`);
          }
        });
      }

      addH('10. Citizenship/Tax Residency');
      addKV('Citizenship 1', formData.citizenship1.citizenship);
      addKV('ID/Passport No. 1', formData.citizenship1.idPassportNo);
      addKV('Country of Issue 1', formData.citizenship1.countryOfIssue);
      addKV('Expiry Date 1', formData.citizenship1.expiryDate || 'N/A');
      if (formData.citizenship2.citizenship) {
        addKV('Citizenship 2', formData.citizenship2.citizenship);
        addKV('ID/Passport No. 2', formData.citizenship2.idPassportNo);
        addKV('Country of Issue 2', formData.citizenship2.countryOfIssue);
        addKV('Expiry Date 2', formData.citizenship2.expiryDate || 'N/A');
      }
      addKV('Country of Taxation 1', formData.countryOfTaxation1 || 'N/A');
      addKV('Tax Identification Number 1', formData.taxId1 || 'N/A');
      if (formData.countryOfTaxation2) {
        addKV('Country of Taxation 2', formData.countryOfTaxation2);
        addKV('Tax Identification Number 2', formData.taxId2 || 'N/A');
      }
      addKV('Non-Domicile Certificate', formData.nonDomicileCertificate ? 'Yes' : 'No');

      addH('11. Legal Status');
      addKV('Authorized Person', formData.authorizedPerson ? 'Yes' : 'No');
      if (formData.authorizedPerson) {
        addKV('Power of Attorney', formData.powerOfAttorney ? 'Yes' : 'No');
      }
      addKV('Nominee', formData.nominee ? 'Yes' : 'No');
      if (formData.nominee) {
        addKV('Trust Deed', formData.trustDeed ? 'Yes' : 'No');
      }
      addKV('Signatory', formData.signatory ? 'Yes' : 'No');
      if (formData.signatory) {
        addKV('Board Approval (BoD Minutes)', formData.boardApproval ? 'Yes' : 'No');
      }

      if (formData.requiresVerification !== false) {
        addH('12. Document Verification');
        addKV('Passport', formData.documents.passport ? 'Yes' : 'No');
        addKV('Identity Card', formData.documents.identityCard ? 'Yes' : 'No');
        addKV('Utility Bill', formData.documents.utilityBill ? 'Yes' : 'No');
        addKV('Bank Reference', formData.documents.bankReference ? 'Yes' : 'No');
        addKV('CV', formData.documents.cv ? 'Yes' : 'No');
        addKV('Trust Deed', formData.documents.trustDeed ? 'Yes' : 'No');
        addKV('Board Minutes', formData.documents.boardMinutes ? 'Yes' : 'No');
        addKV('Power of Attorney', formData.documents.powerOfAttorney ? 'Yes' : 'No');
        addKV('Other Documents', formData.documents.other ? `Yes - ${formData.documents.otherSpecify}` : 'No');
        addKV('SUMSUB Verification', formData.sumsubVerificationCompleted ? 'Completed' : 'Not Completed');
      } else {
        addH('12. Document Verification');
        addKV('Status', 'Not required for selected product');
      }

      addH('13. Signature');
      if (formData.signatureDataUrl) {
        const img = formData.signatureDataUrl;
        doc.addImage(img, 'PNG', margin, y, 60, 24);
        y += 30;
      } else {
        addKV('Signature', 'Not provided');
      }

      const filename = `KYC_Individual_${formData.lastName || 'Form'}.pdf`;
      const blob = doc.output('blob');
      const file = new File([blob], filename, { type: 'application/pdf' });
      doc.save(filename);
      return file;
    } catch (e: any) {
      setError('Failed to generate PDF');
      return null;
      // no rethrow to keep UX smooth
    } finally {
      setLoading(false);
    }
  };

  // KYC handler functions
  const startKYCProcess = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Prepare KYC data
      const kycData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        dateOfBirth: formData.dateOfBirth,
        address: {
          street: formData.address.street,
          city: formData.address.city,
          state: formData.address.state,
          postalCode: formData.address.postalCode,
          country: formData.address.country
        }
      };

      // Use email as username for KYC
      const kycResponse = await kycService.createApplicant(kycData, formData.email);
      console.log('KYC Response:', kycResponse);

      if (kycResponse.success && kycResponse.data) {
        console.log('Setting KYC state:', {
          applicantId: kycResponse.data.applicantId,
          accessToken: kycResponse.data.accessToken ? '***' + kycResponse.data.accessToken.slice(-8) : 'null'
        });
        setKycApplicantId(kycResponse.data.applicantId);
        setKycAccessToken(kycResponse.data.accessToken);
        setShowKycModal(true);
        setFormData(prev => ({ ...prev, sumsubVerificationCompleted: true }));
      } else {
        setError('Failed to create KYC applicant');
      }
    } catch (error: any) {
      console.error('KYC creation error:', error);
      setError('Error creating KYC applicant: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKYCComplete = (status: string) => {
    console.log('KYC completed with status:', status);
    setKycStatus(status as 'pending' | 'completed' | 'rejected');
    setShowKycModal(false);
    
    if (status === 'completed') {
      setFormData(prev => ({ ...prev, sumsubVerificationCompleted: true }));
    }
  };

  const handleKYCError = (error: string) => {
    console.error('KYC error:', error);
    setError(error);
    setShowKycModal(false);
  };

  const handleKYCClose = () => {
    setShowKycModal(false);
  };

  const renderReviewSignStep = () => (
          <div>
            <div ref={reviewRef} style={{ padding: '24px', background: '#ffffff', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <h3 style={{ marginTop: 0, marginBottom: 24 }}>Review All Details</h3>
              
              {/* Product Information */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>1. Selected Product</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Product ID:</strong> {formData.productId}</div>
                  <div><strong>Product Type:</strong> {formData.productType}</div>
                </div>
              </div>
              
              {/* Personal Information */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>2. Personal Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>First Name:</strong> {formData.firstName}</div>
                  <div><strong>Last Name:</strong> {formData.lastName}</div>
                  <div><strong>Email:</strong> {formData.email}</div>
                  <div><strong>Phone:</strong> {formData.phoneNumber}</div>
                  <div><strong>Date of Birth:</strong> {formData.dateOfBirth}</div>
                  <div><strong>Gender:</strong> {formData.gender}</div>
                  <div><strong>Nationality:</strong> {formData.nationality}</div>
                </div>
              </div>

              {/* Identification */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>3. Identification</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>ID/Passport Number:</strong> {formData.idNumber}</div>
                  <div><strong>Country of Issue:</strong> {formData.idCountryOfIssue}</div>
                  <div><strong>Date of Expiry:</strong> {formData.idDateOfExpiry}</div>
                  <div><strong>Social Insurance Number:</strong> {formData.socialInsuranceNumber}</div>
                </div>
              </div>

              {/* Sanctions / Restricted Measures */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>4. Sanctions / Restricted Measures</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Under Sanctions:</strong> {formData.underSanctions ? 'Yes' : 'No'}</div>
                  <div><strong>Sanction Type:</strong> {formData.sanctionType || 'N/A'}</div>
                  <div><strong>Sanction From:</strong> {formData.sanctionFrom || 'N/A'}</div>
                </div>
              </div>

              {/* Citizenship/Tax Residency */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>5. Citizenship/Tax Residency</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Citizenship 1:</strong> {formData.citizenship1.citizenship}</div>
                  <div><strong>ID/Passport 1:</strong> {formData.citizenship1.idPassportNo}</div>
                  <div><strong>Country of Issue 1:</strong> {formData.citizenship1.countryOfIssue}</div>
                  <div><strong>Expiry Date 1:</strong> {formData.citizenship1.expiryDate || 'N/A'}</div>
                  {formData.citizenship2.citizenship && (
                    <>
                      <div><strong>Citizenship 2:</strong> {formData.citizenship2.citizenship}</div>
                      <div><strong>ID/Passport 2:</strong> {formData.citizenship2.idPassportNo}</div>
                      <div><strong>Country of Issue 2:</strong> {formData.citizenship2.countryOfIssue}</div>
                      <div><strong>Expiry Date 2:</strong> {formData.citizenship2.expiryDate || 'N/A'}</div>
                    </>
                  )}
                  <div><strong>Country of Taxation 1:</strong> {formData.countryOfTaxation1 || 'N/A'}</div>
                  <div><strong>Tax ID 1:</strong> {formData.taxId1 || 'N/A'}</div>
                  <div><strong>Country of Taxation 2:</strong> {formData.countryOfTaxation2 || 'N/A'}</div>
                  <div><strong>Tax ID 2:</strong> {formData.taxId2 || 'N/A'}</div>
                  <div><strong>Non-Domicile Certificate:</strong> {formData.nonDomicileCertificate ? 'Yes' : 'No'}</div>
                </div>
              </div>

              {/* Legal Status */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>6. Legal Status</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Authorized Person:</strong> {formData.authorizedPerson ? 'Yes' : 'No'}</div>
                  <div><strong>Power of Attorney:</strong> {formData.powerOfAttorney ? 'Yes' : 'No'}</div>
                  <div><strong>Nominee:</strong> {formData.nominee ? 'Yes' : 'No'}</div>
                  <div><strong>Trust Deed:</strong> {formData.trustDeed ? 'Yes' : 'No'}</div>
                  <div><strong>Signatory:</strong> {formData.signatory ? 'Yes' : 'No'}</div>
                  <div><strong>Board Approval:</strong> {formData.boardApproval ? 'Yes' : 'No'}</div>
                </div>
              </div>

              {/* PEP */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>7. PEP (Politically Exposed Person)</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>PEP:</strong> {formData.pep ? 'Yes' : 'No'}</div>
                </div>
                {formData.pep && formData.pepDetails && formData.pepDetails.some(detail => detail.position || detail.organization || detail.country || detail.untilWhen) && (
                  <div style={{ marginTop: '12px' }}>
                    <strong>Personal PEP Details:</strong>
                    {formData.pepDetails.map((detail, index) => (
                      detail.position || detail.organization || detail.country || detail.untilWhen ? (
                        <div key={index} style={{ marginLeft: '16px', fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                          {index + 1}. {detail.position} at {detail.organization}, {detail.country} (until {detail.untilWhen || 'N/A'})
                        </div>
                      ) : null
                    ))}
                  </div>
                )}
                {formData.familyPep && (
                  <div style={{ marginTop: '12px' }}>
                    <strong>Family PEP:</strong> {formData.familyPepName} ({formData.familyPepRelationship})
                    {formData.familyPepDetails && formData.familyPepDetails.some(detail => detail.position || detail.organization || detail.country || detail.untilWhen) && (
                      <div style={{ marginLeft: '16px' }}>
                        {formData.familyPepDetails.map((detail, index) => (
                          detail.position || detail.organization || detail.country || detail.untilWhen ? (
                            <div key={index} style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                              {index + 1}. {detail.position} at {detail.organization}, {detail.country} (until {detail.untilWhen || 'N/A'})
                            </div>
                          ) : null
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {formData.closeAssociatePep && (
                  <div style={{ marginTop: '12px' }}>
                    <strong>Close Associate PEP:</strong> {formData.closeAssociatePepName} ({formData.closeAssociatePepRelationship})
                    {formData.closeAssociatePepDetails && formData.closeAssociatePepDetails.some(detail => detail.position || detail.organization || detail.country || detail.untilWhen) && (
                      <div style={{ marginLeft: '16px' }}>
                        {formData.closeAssociatePepDetails.map((detail, index) => (
                          detail.position || detail.organization || detail.country || detail.untilWhen ? (
                            <div key={index} style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                              {index + 1}. {detail.position} at {detail.organization}, {detail.country} (until {detail.untilWhen || 'N/A'})
                            </div>
                          ) : null
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Occupational/Business Activity */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>8. Occupational/Business Activity</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Employment Status:</strong> {formData.employmentStatus || 'Not specified'}</div>
                </div>
                {formData.employmentStatus === 'Employed' && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div><strong>Occupation:</strong> {formData.occupation}</div>
                      <div><strong>Position:</strong> {formData.employerPosition}</div>
                      <div><strong>Date of Employment:</strong> {formData.dateOfEmployment}</div>
                      <div><strong>Nature of Business:</strong> {formData.natureOfBusiness}</div>
                      <div><strong>Employer:</strong> {formData.employerName}</div>
                      <div><strong>Education:</strong> {formData.levelOfEducation}</div>
                      <div><strong>Shareholding:</strong> {formData.shareholdingPercentage}%</div>
                      <div><strong>Defence Tax Liable:</strong> {formData.defenceTaxLiable ? 'Yes' : 'No'}</div>
                      <div style={{ gridColumn: '1 / -1' }}><strong>Employer Address:</strong> {formData.employerAddress}, {formData.employerDistrict}, {formData.employerCountry}</div>
                    </div>
                  </div>
                )}
                {formData.employmentStatus === 'Self-Employed' && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div><strong>Years in Business:</strong> {formData.yearsInBusiness}</div>
                      <div><strong>Company Name:</strong> {formData.nonIncorporatedCompanyName}</div>
                      <div><strong>Shareholding:</strong> {formData.selfEmployedShareholding}%</div>
                      <div style={{ gridColumn: '1 / -1' }}><strong>Business Activity Description:</strong> {formData.businessActivityDescription}</div>
                      <div style={{ gridColumn: '1 / -1' }}><strong>Company Address:</strong> {formData.companyAddress}, {formData.companyTown}, {formData.companyPostalCode}</div>
                    </div>
                  </div>
                )}
                {(formData.employmentStatus === 'Retired' || formData.employmentStatus === 'Unemployed' || formData.employmentStatus === 'Other') && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div><strong>Former Employer:</strong> {formData.formerEmployerName}</div>
                      <div><strong>Nature of Business:</strong> {formData.formerNatureOfBusiness}</div>
                      <div><strong>Hire Date:</strong> {formData.hireDate}</div>
                      <div><strong>Termination Date:</strong> {formData.terminationDate}</div>
                      <div><strong>Employment Location:</strong> {formData.employmentLocation}</div>
                      <div><strong>Occupation:</strong> {formData.formerOccupation}</div>
                      <div><strong>Position:</strong> {formData.formerPosition}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Source of Wealth */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>9. Source of Wealth</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Size of Wealth:</strong> {formData.wealthSizeRange}</div>
                  <div><strong>Employment Wealth:</strong> {formData.employmentWealthAmount}</div>
                  <div><strong>Other Investments:</strong> {formData.otherInvestmentsAmount}</div>
                  <div><strong>Business Interests:</strong> {formData.businessInterestsAmount}</div>
                  <div><strong>Real Estate Wealth:</strong> {formData.realEstateWealthAmount}</div>
                  <div><strong>Pension Funds:</strong> {formData.pensionFundsAmount}</div>
                  <div><strong>Inheritance/Gifts:</strong> {formData.inheritanceGiftsAmount}</div>
                  <div><strong>Other Wealth:</strong> {formData.otherWealthAmount}</div>
                  <div><strong>Country of Origin:</strong> {formData.countryOfOriginWealth}</div>
                  <div style={{ gridColumn: '1 / -1' }}><strong>Source of Wealth Explanation:</strong> {formData.sourceOfWealthExplanation}</div>
                  <div><strong>Bankruptcy Filed:</strong> {formData.bankruptcyFiled ? 'Yes' : 'No'}</div>
                  {formData.bankruptcyFiled && (
                    <div style={{ gridColumn: '1 / -1' }}><strong>Bankruptcy Details:</strong> {formData.bankruptcyDetails}</div>
                  )}
                </div>
              </div>

              {/* Declarations */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>10. Declarations</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Source of Funds:</strong> {formData.sourceOfFunds}</div>
                  <div><strong>Purpose of Account:</strong> {formData.purposeOfAccount}</div>
                  <div><strong>Terms Accepted:</strong> {formData.terms ? 'Yes' : 'No'}</div>
                  <div><strong>Marketing Consent:</strong> {formData.marketing ? 'Yes' : 'No'}</div>
                </div>
              </div>

              {/* Document Verification */}
              {verificationRequired && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>11. Document Verification</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Passport:</strong> {formData.documents.passport ? 'Yes' : 'No'}</div>
                  <div><strong>Identity Card Front:</strong> {!!formData.uploadedDocuments?.identityCardFront?.length ? 'Yes' : 'No'}</div>
                  <div><strong>Identity Card Back:</strong> {!!formData.uploadedDocuments?.identityCardBack?.length ? 'Yes' : 'No'}</div>
                  <div><strong>Utility Bill:</strong> {formData.documents.utilityBill ? 'Yes' : 'No'}</div>
                  <div><strong>Bank Reference:</strong> {formData.documents.bankReference ? 'Yes' : 'No'}</div>
                  <div><strong>CV:</strong> {formData.documents.cv ? 'Yes' : 'No'}</div>
                  <div><strong>Trust Deed:</strong> {formData.documents.trustDeed ? 'Yes' : 'No'}</div>
                  <div><strong>Board Minutes:</strong> {formData.documents.boardMinutes ? 'Yes' : 'No'}</div>
                  <div><strong>Power of Attorney:</strong> {formData.documents.powerOfAttorney ? 'Yes' : 'No'}</div>
                  <div><strong>Other:</strong> {formData.documents.other ? `Yes - ${formData.documents.otherSpecify}` : 'No'}</div>
                  <div><strong>SUMSUB Verification:</strong> {formData.sumsubVerificationCompleted ? 'Completed' : 'Not Completed'}</div>
                </div>
              </div>
              )}
            </div>
            <div className="form-group" style={{ marginTop: '24px' }}>
              <label className="form-label">Digital Signature</label>
              <SignaturePad onChange={(data) => handleChange('signatureDataUrl', data)} />
              <div style={{ marginTop: 8, color: '#6b7280', fontSize: 12 }}>Please sign inside the box. Your signature will be embedded in the PDF.</div>
            </div>
          </div>
  );

  const renderStep = () => {
    const input = (label: string, path: string, type: string = 'text', placeholder?: string) => (
      <div className="form-group">
        <label className="form-label">{label}</label>
        <input
          type={type}
          className="form-input"
          value={path.split('.').reduce<any>((a, c) => a[c], formData) || ''}
          onChange={(e) => handleChange(path, type === 'number' ? Number(e.target.value) : e.target.value)}
          placeholder={placeholder}
        />
      </div>
    );

    switch (activeStep) {
      case 0:
        return (
          <ProductSelection
            customerType="PERSONAL"
            onProductSelect={(productId: number, productType: string, registrationFee?: number, registrationFeeType?: 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY', requiresVerification?: boolean) => {
              const needsVerification = requiresVerification !== false;
              const feeType = registrationFeeType || 'FIXED_ONE_OFF';
              setFormData(prev => ({
                ...prev,
                productId,
                productType,
                registrationFee: Number(registrationFee || 0),
                registrationFeeType: feeType,
                requiresVerification: needsVerification,
                sumsubVerificationCompleted: !needsVerification ? true : prev.sumsubVerificationCompleted
              }));
              setPaymentCompleted(feeType === 'NO_FEE');
              setPaymentStatus(feeType === 'NO_FEE' ? 'COMPLETED' : 'PENDING');
            }}
            selectedProductId={formData.productId}
          />
        );
      case 1:
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
            {input('First Name', 'firstName')}
            {input('Last Name', 'lastName')}
            {input('Email', 'email', 'email')}
            <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '-8px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" onClick={sendVerificationLink} disabled={emailVerificationLoading || !formData.email} className="btn-secondary">
                  {emailVerificationLoading ? 'Sending...' : 'Send verification link'}
                </button>
                {emailVerificationSent && (
                  <button type="button" onClick={refreshEmailVerificationStatus} disabled={emailVerificationLoading} className="btn-secondary">
                    I verified, check status
                  </button>
                )}
              </div>
              {(emailVerificationMessage || emailVerified) && (
                <p style={{ marginTop: '8px', marginBottom: 0, color: emailVerified ? '#16a34a' : '#6b7280', fontSize: '13px' }}>
                  {emailVerified ? 'Email verified. You can continue.' : emailVerificationMessage}
                </p>
              )}
            </div>
            {input('Phone Number', 'phoneNumber', 'tel')}
            {input('Date of Birth', 'dateOfBirth', 'date')}
            {input('Nationality', 'nationality')}
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select 
                className="form-input" 
                value={formData.gender} 
                onChange={(e) => handleChange('gender', e.target.value)}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>
        );
      case 2:
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
            {input('ID/Passport Number', 'idNumber')}
            {input('Country of Issue', 'idCountryOfIssue')}
            {input('Date of Expiry', 'idDateOfExpiry', 'date')}
            {input('Social Insurance Number', 'socialInsuranceNumber')}
          </div>
        );
      case 3:
        return (
          <div>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px' }}>
              <h3 style={{ margin: 0, marginBottom: 12, color: '#1f2937' }}>Sanctions / Restricted Measures</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Are you under sanctions?</label>
                  <select className="form-input" value={formData.underSanctions ? 'yes' : 'no'} onChange={(e) => handleChange('underSanctions', e.target.value === 'yes')}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div />
                {formData.underSanctions && (
                  <>
                    {input('Specify type of sanctions', 'sanctionType')}
                    {input('From when?', 'sanctionFrom', 'date')}
                  </>
                )}
              </div>

              <div style={{ marginTop: 8 }}>
                <label className="form-label">Is any immediate family member or close associate under sanctions?</label>
                <select className="form-input" value={formData.familySanctions ? 'yes' : 'no'} onChange={(e) => handleChange('familySanctions', e.target.value === 'yes')}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
                {formData.familySanctions && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>Please fill the following:</div>
                    {(formData.familySanctionsList || []).map((row, idx) => (
                      <div key={`fam-${idx}`} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: 8 }}>
                        <input className="form-input" placeholder="Name of Legal Entity/Individual" value={row.name} onChange={(e) => {
                          const list = [...formData.familySanctionsList];
                          list[idx] = { ...list[idx], name: e.target.value };
                          handleChange('familySanctionsList', list);
                        }} />
                        <input className="form-input" placeholder="Country" value={row.country} onChange={(e) => {
                          const list = [...formData.familySanctionsList];
                          list[idx] = { ...list[idx], country: e.target.value };
                          handleChange('familySanctionsList', list);
                        }} />
                        <input className="form-input" placeholder="From when?" type="date" value={row.from} onChange={(e) => {
                          const list = [...formData.familySanctionsList];
                          list[idx] = { ...list[idx], from: e.target.value };
                          handleChange('familySanctionsList', list);
                        }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16 }}>
                <label className="form-label">Do you trade with legal entities or individuals that are under sanctions?</label>
                <select className="form-input" value={formData.tradingWithSanctioned ? 'yes' : 'no'} onChange={(e) => handleChange('tradingWithSanctioned', e.target.value === 'yes')}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
                {formData.tradingWithSanctioned && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>Please fill the following:</div>
                    {(formData.tradingSanctionsList || []).map((row, idx) => (
                      <div key={`trade-${idx}`} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: 8 }}>
                        <input className="form-input" placeholder="Name of Legal Entity/Individual" value={row.name} onChange={(e) => {
                          const list = [...formData.tradingSanctionsList];
                          list[idx] = { ...list[idx], name: e.target.value };
                          handleChange('tradingSanctionsList', list);
                        }} />
                        <input className="form-input" placeholder="Country" value={row.country} onChange={(e) => {
                          const list = [...formData.tradingSanctionsList];
                          list[idx] = { ...list[idx], country: e.target.value };
                          handleChange('tradingSanctionsList', list);
                        }} />
                        <input className="form-input" placeholder="From when?" type="date" value={row.from} onChange={(e) => {
                          const list = [...formData.tradingSanctionsList];
                          list[idx] = { ...list[idx], from: e.target.value };
                          handleChange('tradingSanctionsList', list);
                        }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
              <h3 style={{ marginTop: 0, marginBottom: 16, color: '#1f2937' }}>Citizenship/Tax Residency</h3>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: 20 }}>
                <p style={{ margin: '0 0 8px 0' }}>1. For Cypriots, the I.D. is mandatory.</p>
                <p style={{ margin: '0 0 8px 0' }}>2. In case of multiple citizenships, complete where appropriate.</p>
                <p style={{ margin: '0 0 16px 0' }}>3. If residency is outside of country of origin please provide residency card or any other official documentation as proof.</p>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
                <div className="form-group">
                  <label className="form-label">Citizenship 1 *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.citizenship1.citizenship}
                    onChange={(e) => handleChange('citizenship1.citizenship', e.target.value)}
                    placeholder="Enter citizenship"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">I.D./Passport No. *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.citizenship1.idPassportNo}
                    onChange={(e) => handleChange('citizenship1.idPassportNo', e.target.value)}
                    placeholder="Enter ID/Passport number"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Country of issue *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.citizenship1.countryOfIssue}
                    onChange={(e) => handleChange('citizenship1.countryOfIssue', e.target.value)}
                    placeholder="Enter country of issue"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Expiry date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.citizenship1.expiryDate}
                    onChange={(e) => handleChange('citizenship1.expiryDate', e.target.value)}
                  />
                </div>
              </div>

              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Citizenship 2 (Optional)</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
                  <div className="form-group">
                    <label className="form-label">Citizenship 2</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.citizenship2.citizenship}
                      onChange={(e) => handleChange('citizenship2.citizenship', e.target.value)}
                      placeholder="Enter second citizenship"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">I.D./Passport No.</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.citizenship2.idPassportNo}
                      onChange={(e) => handleChange('citizenship2.idPassportNo', e.target.value)}
                      placeholder="Enter ID/Passport number"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Country of issue</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.citizenship2.countryOfIssue}
                      onChange={(e) => handleChange('citizenship2.countryOfIssue', e.target.value)}
                      placeholder="Enter country of issue"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Expiry date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.citizenship2.expiryDate}
                      onChange={(e) => handleChange('citizenship2.expiryDate', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Tax Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
                  <div className="form-group">
                    <label className="form-label">Country of Taxation 1</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.countryOfTaxation1}
                      onChange={(e) => handleChange('countryOfTaxation1', e.target.value)}
                      placeholder="Enter country of taxation"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tax Identification Number 1</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.taxId1}
                      onChange={(e) => handleChange('taxId1', e.target.value)}
                      placeholder="Enter tax ID number"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Country of Taxation 2</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.countryOfTaxation2}
                      onChange={(e) => handleChange('countryOfTaxation2', e.target.value)}
                      placeholder="Enter second country of taxation"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tax Identification Number 2</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.taxId2}
                      onChange={(e) => handleChange('taxId2', e.target.value)}
                      placeholder="Enter second tax ID number"
                    />
                  </div>
                </div>
                
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Additional Tax Information</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <label style={{ fontSize: '15px', color: '#1f2937', fontWeight: '500' }}>
                      Do you hold a Non-Domicile Certificate?
                    </label>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f2937' }}>
                        <input
                          type="radio"
                          name="nonDomicileCertificate"
                          checked={formData.nonDomicileCertificate === true}
                          onChange={() => handleChange('nonDomicileCertificate', true)}
                          style={{ width: '16px', height: '16px' }}
                        />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f2937' }}>
                        <input
                          type="radio"
                          name="nonDomicileCertificate"
                          checked={formData.nonDomicileCertificate === false}
                          onChange={() => handleChange('nonDomicileCertificate', false)}
                          style={{ width: '16px', height: '16px' }}
                        />
                        No
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
              <h3 style={{ marginTop: 0, marginBottom: 16, color: '#1f2937' }}>Legal Status Declarations</h3>
              <p style={{ color: '#6b7280', marginBottom: 20 }}>Please answer the following questions about your legal status and authority:</p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0' }}>
                  <input 
                    type="checkbox" 
                    style={{ width: '20px', height: '20px', marginTop: '2px' }} 
                    checked={formData.authorizedPerson} 
                    onChange={(e) => handleChange('authorizedPerson', e.target.checked)} 
                  />
                  <div>
                    <label style={{ fontSize: '15px', color: '#1f2937' }}>Are you an authorized person?</label>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>Check if you are acting on behalf of another person or entity</div>
                  </div>
                </div>

                {formData.authorizedPerson && (
                  <div style={{ marginLeft: '32px', padding: '12px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '8px 0' }}>
                      <input 
                        type="checkbox" 
                        style={{ width: '20px', height: '20px', marginTop: '2px' }} 
                        checked={formData.powerOfAttorney} 
                        onChange={(e) => handleChange('powerOfAttorney', e.target.checked)} 
                      />
                      <div>
                        <label style={{ fontSize: '14px', color: '#1f2937' }}>Do you hold a power of attorney?</label>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>Required if you are an authorized person</div>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0', borderTop: '1px solid #e2e8f0' }}>
                  <input 
                    type="checkbox" 
                    style={{ width: '20px', height: '20px', marginTop: '2px' }} 
                    checked={formData.nominee} 
                    onChange={(e) => handleChange('nominee', e.target.checked)} 
                  />
                  <div>
                    <label style={{ fontSize: '15px', color: '#1f2937' }}>Are you a nominee?</label>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>Check if you are holding assets on behalf of another person</div>
                  </div>
                </div>

                {formData.nominee && (
                  <div style={{ marginLeft: '32px', padding: '12px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '8px 0' }}>
                      <input 
                        type="checkbox" 
                        style={{ width: '20px', height: '20px', marginTop: '2px' }} 
                        checked={formData.trustDeed} 
                        onChange={(e) => handleChange('trustDeed', e.target.checked)} 
                      />
                      <div>
                        <label style={{ fontSize: '14px', color: '#1f2937' }}>Do you hold a trust deed?</label>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>Required if you are a nominee</div>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0', borderTop: '1px solid #e2e8f0' }}>
                  <input 
                    type="checkbox" 
                    style={{ width: '20px', height: '20px', marginTop: '2px' }} 
                    checked={formData.signatory} 
                    onChange={(e) => handleChange('signatory', e.target.checked)} 
                  />
                  <div>
                    <label style={{ fontSize: '15px', color: '#1f2937' }}>Are you a signatory?</label>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>Check if you have signing authority for an organization</div>
                  </div>
                </div>

                {formData.signatory && (
                  <div style={{ marginLeft: '32px', padding: '12px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '8px 0' }}>
                      <input 
                        type="checkbox" 
                        style={{ width: '20px', height: '20px', marginTop: '2px' }} 
                        checked={formData.boardApproval} 
                        onChange={(e) => handleChange('boardApproval', e.target.checked)} 
                      />
                      <div>
                        <label style={{ fontSize: '14px', color: '#1f2937' }}>Do you have approval of appointment (minutes) by the Board of Directors (BoD)?</label>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>Required if you are a signatory</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 6:
        return (
          <div>
            {/* Section 1: Personal PEP */}
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Are you a Politically Exposed Person (PEP)?</h4>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="pep" 
                    checked={formData.pep === true} 
                    onChange={() => handleChange('pep', true)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span>Yes</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="pep" 
                    checked={formData.pep === false} 
                    onChange={() => handleChange('pep', false)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span>No</span>
                </label>
              </div>
              
              {formData.pep && (
                <div>
                  <h5 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>If Yes, please fill the following:</h5>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6' }}>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>No.</th>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Position/Organization</th>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Country</th>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Until when?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(formData.pepDetails || []).map((detail, index) => (
                          <tr key={index} style={{ background: index % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                            <td style={{ border: '1px solid #000', padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                              {index + 1}.
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>
                              <input
                                type="text"
                                className="form-input"
                                value={detail.position}
                                onChange={(e) => {
                                  const updatedDetails = [...formData.pepDetails];
                                  updatedDetails[index] = { ...updatedDetails[index], position: e.target.value };
                                  handleChange('pepDetails', updatedDetails);
                                }}
                                placeholder="Enter position/organization"
                                style={{ border: 'none', background: 'transparent', width: '100%', padding: '4px' }}
                              />
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>
                              <input
                                type="text"
                                className="form-input"
                                value={detail.country}
                                onChange={(e) => {
                                  const updatedDetails = [...formData.pepDetails];
                                  updatedDetails[index] = { ...updatedDetails[index], country: e.target.value };
                                  handleChange('pepDetails', updatedDetails);
                                }}
                                placeholder="Enter country"
                                style={{ border: 'none', background: 'transparent', width: '100%', padding: '4px' }}
                              />
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>
                              <input
                                type="date"
                                className="form-input"
                                value={detail.untilWhen}
                                onChange={(e) => {
                                  const updatedDetails = [...formData.pepDetails];
                                  updatedDetails[index] = { ...updatedDetails[index], untilWhen: e.target.value };
                                  handleChange('pepDetails', updatedDetails);
                                }}
                                style={{ border: 'none', background: 'transparent', width: '100%', padding: '4px' }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Section 2: Family PEP */}
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Does any of your Immediate Family Members exercises or have exercised in the past a public function?</h4>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="familyPep" 
                    checked={formData.familyPep === true} 
                    onChange={() => handleChange('familyPep', true)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span>Yes</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="familyPep" 
                    checked={formData.familyPep === false} 
                    onChange={() => handleChange('familyPep', false)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span>No</span>
                </label>
              </div>
              
              {formData.familyPep && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                    {input('Name of PEP:', 'familyPepName')}
                    {input('Relationship to you:', 'familyPepRelationship')}
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6' }}>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>No.</th>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Position/Organization</th>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Country</th>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Until when?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(formData.familyPepDetails || []).map((detail, index) => (
                          <tr key={index} style={{ background: index % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                            <td style={{ border: '1px solid #000', padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                              {index + 1}.
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>
                              <input
                                type="text"
                                className="form-input"
                                value={detail.position}
                                onChange={(e) => {
                                  const updatedDetails = [...formData.familyPepDetails];
                                  updatedDetails[index] = { ...updatedDetails[index], position: e.target.value };
                                  handleChange('familyPepDetails', updatedDetails);
                                }}
                                placeholder="Enter position/organization"
                                style={{ border: 'none', background: 'transparent', width: '100%', padding: '4px' }}
                              />
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>
                              <input
                                type="text"
                                className="form-input"
                                value={detail.country}
                                onChange={(e) => {
                                  const updatedDetails = [...formData.familyPepDetails];
                                  updatedDetails[index] = { ...updatedDetails[index], country: e.target.value };
                                  handleChange('familyPepDetails', updatedDetails);
                                }}
                                placeholder="Enter country"
                                style={{ border: 'none', background: 'transparent', width: '100%', padding: '4px' }}
                              />
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>
                              <input
                                type="date"
                                className="form-input"
                                value={detail.untilWhen}
                                onChange={(e) => {
                                  const updatedDetails = [...formData.familyPepDetails];
                                  updatedDetails[index] = { ...updatedDetails[index], untilWhen: e.target.value };
                                  handleChange('familyPepDetails', updatedDetails);
                                }}
                                style={{ border: 'none', background: 'transparent', width: '100%', padding: '4px' }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Section 3: Close Associate PEP */}
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Are you a Close Associate of a PEP?</h4>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="closeAssociatePep" 
                    checked={formData.closeAssociatePep === true} 
                    onChange={() => handleChange('closeAssociatePep', true)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span>Yes</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="closeAssociatePep" 
                    checked={formData.closeAssociatePep === false} 
                    onChange={() => handleChange('closeAssociatePep', false)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span>No</span>
                </label>
              </div>
              
              {formData.closeAssociatePep && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                    {input('Name of PEP:', 'closeAssociatePepName')}
                    {input('Relationship:', 'closeAssociatePepRelationship')}
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6' }}>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>No.</th>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Position/Organization</th>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Country</th>
                          <th style={{ border: '1px solid #000', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Until when?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(formData.closeAssociatePepDetails || []).map((detail, index) => (
                          <tr key={index} style={{ background: index % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                            <td style={{ border: '1px solid #000', padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                              {index + 1}.
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>
                              <input
                                type="text"
                                className="form-input"
                                value={detail.position}
                                onChange={(e) => {
                                  const updatedDetails = [...formData.closeAssociatePepDetails];
                                  updatedDetails[index] = { ...updatedDetails[index], position: e.target.value };
                                  handleChange('closeAssociatePepDetails', updatedDetails);
                                }}
                                placeholder="Enter position/organization"
                                style={{ border: 'none', background: 'transparent', width: '100%', padding: '4px' }}
                              />
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>
                              <input
                                type="text"
                                className="form-input"
                                value={detail.country}
                                onChange={(e) => {
                                  const updatedDetails = [...formData.closeAssociatePepDetails];
                                  updatedDetails[index] = { ...updatedDetails[index], country: e.target.value };
                                  handleChange('closeAssociatePepDetails', updatedDetails);
                                }}
                                placeholder="Enter country"
                                style={{ border: 'none', background: 'transparent', width: '100%', padding: '4px' }}
                              />
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>
                              <input
                                type="date"
                                className="form-input"
                                value={detail.untilWhen}
                                onChange={(e) => {
                                  const updatedDetails = [...formData.closeAssociatePepDetails];
                                  updatedDetails[index] = { ...updatedDetails[index], untilWhen: e.target.value };
                                  handleChange('closeAssociatePepDetails', updatedDetails);
                                }}
                                style={{ border: 'none', background: 'transparent', width: '100%', padding: '4px' }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 7:
        return (
          <div>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
              <h3 style={{ marginTop: 0, marginBottom: 16, color: '#1f2937' }}>Occupational/Business Activity</h3>
              <p style={{ color: '#6b7280', marginBottom: 20 }}>Please select your employment status and complete relevant sections only.</p>
              
              <div style={{ marginBottom: '24px' }}>
                <label className="form-label">Please select your employment status:</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                  {['Employed', 'Self-Employed', 'Retired', 'Unemployed', 'Other'].map((status) => (
                    <label key={status} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f2937' }}>
                      <input
                        type="radio"
                        name="employmentStatus"
                        value={status}
                        checked={formData.employmentStatus === status}
                        onChange={(e) => handleChange('employmentStatus', e.target.value)}
                        style={{ width: '16px', height: '16px' }}
                      />
                      {status}
                    </label>
                  ))}
                </div>
              </div>

              {formData.employmentStatus === 'Employed' && (
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>D.1 Employed</h4>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                    Note: If current employment is less than 5 years, complete section D.3 additionally.
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
                    <div className="form-group">
                      <label className="form-label">Occupation</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.occupation}
                        onChange={(e) => handleChange('occupation', e.target.value)}
                        placeholder="Enter your occupation"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Position</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.employerPosition}
                        onChange={(e) => handleChange('employerPosition', e.target.value)}
                        placeholder="Enter your position"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Date of Employment</label>
                      <input
                        type="date"
                        className="form-input"
                        value={formData.dateOfEmployment}
                        onChange={(e) => handleChange('dateOfEmployment', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nature of Business</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.natureOfBusiness}
                        onChange={(e) => handleChange('natureOfBusiness', e.target.value)}
                        placeholder="Enter nature of business"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Employer's Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.employerName}
                        onChange={(e) => handleChange('employerName', e.target.value)}
                        placeholder="Enter employer's name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Level of Education</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.levelOfEducation}
                        onChange={(e) => handleChange('levelOfEducation', e.target.value)}
                        placeholder="Enter level of education"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Employer's Address</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.employerAddress}
                        onChange={(e) => handleChange('employerAddress', e.target.value)}
                        placeholder="Enter employer's address"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.employerCountry}
                        onChange={(e) => handleChange('employerCountry', e.target.value)}
                        placeholder="Enter country"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">District</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.employerDistrict}
                        onChange={(e) => handleChange('employerDistrict', e.target.value)}
                        placeholder="Enter district"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Shareholding (%) in Employer's entity (if applicable)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.shareholdingPercentage}
                        onChange={(e) => handleChange('shareholdingPercentage', e.target.value)}
                        placeholder="Enter shareholding percentage"
                      />
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <label style={{ fontSize: '15px', color: '#1f2937', fontWeight: '500' }}>
                        Are you liable to pay defence tax in Cyprus?
                      </label>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f2937' }}>
                          <input
                            type="radio"
                            name="defenceTaxLiable"
                            checked={formData.defenceTaxLiable === true}
                            onChange={() => handleChange('defenceTaxLiable', true)}
                            style={{ width: '16px', height: '16px' }}
                          />
                          Yes
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f2937' }}>
                          <input
                            type="radio"
                            name="defenceTaxLiable"
                            checked={formData.defenceTaxLiable === false}
                            onChange={() => handleChange('defenceTaxLiable', false)}
                            style={{ width: '16px', height: '16px' }}
                          />
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {formData.employmentStatus === 'Self-Employed' && (
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>D.2 Self-Employed</h4>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                    Note: If current self-employment is less than 5 years, complete section D.3 additionally.
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
                    <div className="form-group">
                      <label className="form-label">Years in business:</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.yearsInBusiness}
                        onChange={(e) => handleChange('yearsInBusiness', e.target.value)}
                        placeholder="Enter years in business"
                      />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">Please provide a detailed description of Client's occupation/business activity</label>
                      <textarea
                        className="form-input"
                        value={formData.businessActivityDescription}
                        onChange={(e) => handleChange('businessActivityDescription', e.target.value)}
                        placeholder="Enter detailed description of business activity"
                        rows={4}
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                      If business activity is carried out through a non-incorporated entity, please complete also the below:
                    </p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
                      <div className="form-group">
                        <label className="form-label">Non-incorporated Company Name:</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.nonIncorporatedCompanyName}
                          onChange={(e) => handleChange('nonIncorporatedCompanyName', e.target.value)}
                          placeholder="Enter company name"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Shareholding (%) in Employer's entity (if applicable):</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.selfEmployedShareholding}
                          onChange={(e) => handleChange('selfEmployedShareholding', e.target.value)}
                          placeholder="Enter shareholding percentage"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Company's Address (Trading/Head Office), Street Name & No.:</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.companyAddress}
                          onChange={(e) => handleChange('companyAddress', e.target.value)}
                          placeholder="Enter street name and number"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Postal Code:</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.companyPostalCode}
                          onChange={(e) => handleChange('companyPostalCode', e.target.value)}
                          placeholder="Enter postal code"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Town:</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.companyTown}
                          onChange={(e) => handleChange('companyTown', e.target.value)}
                          placeholder="Enter town"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(formData.employmentStatus === 'Retired' || formData.employmentStatus === 'Unemployed' || formData.employmentStatus === 'Other') && (
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>D.3 Retired, Unemployed, Other (Previous Occupation/Activity)</h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
                    <div className="form-group">
                      <label className="form-label">Former employer's name:</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.formerEmployerName}
                        onChange={(e) => handleChange('formerEmployerName', e.target.value)}
                        placeholder="Enter former employer's name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nature of business:</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.formerNatureOfBusiness}
                        onChange={(e) => handleChange('formerNatureOfBusiness', e.target.value)}
                        placeholder="Enter nature of business"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Hire Date:</label>
                      <input
                        type="date"
                        className="form-input"
                        value={formData.hireDate}
                        onChange={(e) => handleChange('hireDate', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Location/Country of employment:</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.employmentLocation}
                        onChange={(e) => handleChange('employmentLocation', e.target.value)}
                        placeholder="Enter location/country"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Occupation and Position:</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.formerOccupation}
                        onChange={(e) => handleChange('formerOccupation', e.target.value)}
                        placeholder="Enter occupation"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Position:</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.formerPosition}
                        onChange={(e) => handleChange('formerPosition', e.target.value)}
                        placeholder="Enter position"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Termination Date:</label>
                      <input
                        type="date"
                        className="form-input"
                        value={formData.terminationDate}
                        onChange={(e) => handleChange('terminationDate', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 8:
        return (
          <div>
            <h3>E.2 Source of Wealth (Total Assets)</h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              Note: Source of Wealth is the origin of all the money a person has accumulated over their lifetime. This section is to describe the activities that have contributed toward the total accumulated wealth (e.g. how the wealth has been created). This section is mandatory to be completed.
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Please specify the Size of Wealth:</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                {[
                  'less than or equal to €100.000',
                  '€100.001 - €500.000',
                  '€500.001 - €1.000.000',
                  '€1.000.001 - €5.000.000',
                  'Over €5.000.000'
                ].map((range) => (
                  <label key={range} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="wealthSizeRange"
                      value={range}
                      checked={formData.wealthSizeRange === range}
                      onChange={(e) => handleChange('wealthSizeRange', e.target.value)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span>{range}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Please specify the Source of Wealth:</label>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Note: Approximate amounts are required.</p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '200px' }}>Employment:</span>
                  <span>€</span>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.employmentWealthAmount}
                    onChange={(e) => handleChange('employmentWealthAmount', e.target.value)}
                    placeholder="Enter amount"
                    style={{ width: '120px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '200px' }}>Other Investments (i.e. interest, dividends from stocks, bonds, royalties etc.):</span>
                  <span>€</span>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.otherInvestmentsAmount}
                    onChange={(e) => handleChange('otherInvestmentsAmount', e.target.value)}
                    placeholder="Enter amount"
                    style={{ width: '120px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '200px' }}>Business Interests: (i.e shareholding/partner in a firm earning dividends):</span>
                  <span>€</span>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.businessInterestsAmount}
                    onChange={(e) => handleChange('businessInterestsAmount', e.target.value)}
                    placeholder="Enter amount"
                    style={{ width: '120px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '200px' }}>Real Estate Income:</span>
                  <span>€</span>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.realEstateWealthAmount}
                    onChange={(e) => handleChange('realEstateWealthAmount', e.target.value)}
                    placeholder="Enter amount"
                    style={{ width: '120px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '200px' }}>Expire of Pension Funds:</span>
                  <span>€</span>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.pensionFundsAmount}
                    onChange={(e) => handleChange('pensionFundsAmount', e.target.value)}
                    placeholder="Enter amount"
                    style={{ width: '120px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '200px' }}>Inheritance/Gifts:</span>
                  <span>€</span>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.inheritanceGiftsAmount}
                    onChange={(e) => handleChange('inheritanceGiftsAmount', e.target.value)}
                    placeholder="Enter amount"
                    style={{ width: '120px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '200px' }}>Other (specify):</span>
                  <span>€</span>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.otherWealthAmount}
                    onChange={(e) => handleChange('otherWealthAmount', e.target.value)}
                    placeholder="Enter amount"
                    style={{ width: '120px' }}
                  />
                  <input
                    type="text"
                    className="form-input"
                    value={formData.otherWealthSpecify}
                    onChange={(e) => handleChange('otherWealthSpecify', e.target.value)}
                    placeholder="Specify other source"
                    style={{ width: '150px' }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Please list the country(ies) of origin of wealth/activities:</label>
              <input
                type="text"
                className="form-input"
                value={formData.countryOfOriginWealth}
                onChange={(e) => handleChange('countryOfOriginWealth', e.target.value)}
                placeholder="Enter countries"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Explanation of the Source of Wealth:</label>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  <li><strong>For investment income:</strong> State if these are dividends received from shares held in other companies (provide the name) / if proceeds from sale of investment, state the name and date of transaction. If you hold a portfolio of investments, state the brokers/fund details.</li>
                  <li><strong>If wealth is generated from Real Estate:</strong> Specify if this has been generated from rental income or receipts from sale of property (state property details).</li>
                  <li><strong>For Redemption, Pension Funds/Life Insurance:</strong> State pension fund and insurance name.</li>
                  <li><strong>For wealth resulting from an inheritance:</strong> State from who and what was inherited.</li>
                  <li><strong>For other:</strong> Clarify with details.</li>
                  <li><strong>Note:</strong> Evidence may be requested if deemed necessary from the KYC/AML procedures.</li>
                </ul>
              </div>
              <textarea
                className="form-input"
                value={formData.sourceOfWealthExplanation}
                onChange={(e) => handleChange('sourceOfWealthExplanation', e.target.value)}
                placeholder="Please provide detailed explanation..."
                rows={8}
                style={{ width: '100%', resize: 'vertical', minHeight: '180px' }}
              />
            </div>

            {/* E.3 Other Provisions */}
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginTop: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>E.3 Other Provisions</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                Note: This section is mandatory to be completed.
              </p>
              
              <div style={{ marginBottom: '20px' }}>
                <label className="form-label">Have you ever filed for bankruptcy?</label>
                <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="bankruptcyFiled"
                      checked={formData.bankruptcyFiled === true}
                      onChange={() => handleChange('bankruptcyFiled', true)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span>Yes</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="bankruptcyFiled"
                      checked={formData.bankruptcyFiled === false}
                      onChange={() => handleChange('bankruptcyFiled', false)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span>No</span>
                  </label>
                </div>
                
                {formData.bankruptcyFiled && (
                  <div style={{ marginTop: '12px' }}>
                    <label className="form-label">If yes, please provide more details:</label>
                    <textarea
                      className="form-input"
                      value={formData.bankruptcyDetails}
                      onChange={(e) => handleChange('bankruptcyDetails', e.target.value)}
                      placeholder="Please provide details about the bankruptcy..."
                      rows={3}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 9:
        return (
          <div>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0' }}>
                <input type="checkbox" style={{ width: '20px', height: '20px', marginTop: '2px' }} checked={formData.terms} onChange={(e) => handleChange('terms', e.target.checked)} />
                <div>
                  <label style={{ fontSize: '15px', color: '#1f2937' }}>I agree to the Terms and Privacy Policy</label>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>Required to proceed with registration</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0', borderTop: '1px solid #e2e8f0' }}>
                <input type="checkbox" style={{ width: '20px', height: '20px', marginTop: '2px' }} checked={formData.marketing} onChange={(e) => handleChange('marketing', e.target.checked)} />
                <div>
                  <label style={{ fontSize: '15px', color: '#1f2937' }}>I consent to receive marketing communications</label>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>Optional - you can unsubscribe at any time</div>
                </div>
              </div>

              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, marginBottom: 8, color: '#1f2937' }}>Registration Payment</h4>
                <p style={{ marginTop: 0, color: '#475569' }}>
                  {formData.registrationFeeType === 'NO_FEE' && 'No registration payment is required for your selected tier.'}
                  {formData.registrationFeeType === 'FIXED_ONE_OFF' && <>A one-time registration fee of <strong>EUR {Number(formData.registrationFee || 0).toFixed(2)}</strong> for your selected tier is required before account creation.</>}
                  {formData.registrationFeeType === 'RECURRING_MONTHLY' && <>A recurring monthly fee of <strong>EUR {Number(formData.registrationFee || 0).toFixed(2)}</strong> for your selected tier is required before account creation.</>}
                </p>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {formData.registrationFeeType !== 'NO_FEE' && (
                    <button
                      type="button"
                      onClick={handleRegistrationPayment}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <FaCcStripe />
                        {formData.registrationFeeType === 'RECURRING_MONTHLY' ? 'Start Monthly Subscription' : 'Pay Registration Fee'}
                      </span>
                    </button>
                  )}
                  <span style={{ color: paymentCompleted ? '#16a34a' : '#475569' }}>
                    Status: <strong>{paymentStatus}</strong>
                  </span>
                </div>
                {paymentSessionId && <p style={{ marginBottom: 0, fontSize: 12, color: '#64748b' }}>Session: {paymentSessionId}</p>}
              </div>
            </div>
          </div>
        );
      case 10:
        if (!verificationRequired) {
          return renderReviewSignStep();
        }
        return (
          <div>
            {/* Note Section */}
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Note</h3>
              <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 12px 0' }}>1. It is mandatory to attach a minimum of an ID (for EU Nationals) or passport (for non-EU Nationals), along with a utility bill or another document as proof of residential address.</p>
                <p style={{ margin: '0 0 12px 0' }}>2. A power of attorney, trust deed, or minutes of the Board of Directors (BoD) should be attached as appropriate.</p>
                <p style={{ margin: '0 0 12px 0' }}>3. Attached documents should be original or certified as true copies of the original by a notary or apostille.</p>
                <p style={{ margin: '0 0 0 0' }}>4. Additional documentation may be requested as evidence of information given in the questionnaire.</p>
              </div>
            </div>

            {/* Document Selection Table */}
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Please complete the table below:</h3>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db' }}>
                  <thead>
                    <tr style={{ background: '#f3f4f6' }}>
                      <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Select:</th>
                      <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Documents:</th>
                      <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Comments (if any):</th>
                      <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Upload:</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'passport', label: 'Passport', multiple: false },
                      { key: 'identityCardFront', label: 'Identity Card Front', multiple: false },
                      { key: 'identityCardBack', label: 'Identity Card Back', multiple: false },
                      { key: 'utilityBill', label: 'Utility Bill (dated within 6 months)', multiple: false },
                      { key: 'bankReference', label: 'Bank Reference', multiple: false },
                      { key: 'cv', label: 'C.V. (signed)', multiple: false },
                      { key: 'trustDeed', label: 'Trust deed', multiple: false },
                      { key: 'boardMinutes', label: 'Minutes of approval of Signatory by BoD', multiple: false },
                      { key: 'powerOfAttorney', label: 'Power of attorney', multiple: false },
                      { key: 'other', label: 'Other (specify)', multiple: false }
                    ].map((doc, index) => (
                      <tr key={doc.key} style={{ background: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                        <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={doc.key === 'identityCardFront' ? !!formData.uploadedDocuments?.identityCardFront?.length : 
                                    doc.key === 'identityCardBack' ? !!formData.uploadedDocuments?.identityCardBack?.length :
                                    formData.documents[doc.key as keyof typeof formData.documents] as boolean}
                            onChange={(e) => handleChange(`documents.${doc.key}`, e.target.checked)}
                            style={{ width: '16px', height: '16px' }}
                          />
                        </td>
                        <td style={{ border: '1px solid #d1d5db', padding: '12px' }}>
                          {doc.label}
                          {doc.key === 'other' && formData.documents.other && (
                            <input
                              type="text"
                              className="form-input"
                              value={formData.documents.otherSpecify}
                              onChange={(e) => handleChange('documents.otherSpecify', e.target.value)}
                              placeholder="Specify other document"
                              style={{ width: '200px', marginTop: '8px' }}
                            />
                          )}
                        </td>
                        <td style={{ border: '1px solid #d1d5db', padding: '8px' }}>
                          <input
                            type="text"
                            className="form-input"
                            value={formData.documentComments[doc.key as keyof typeof formData.documentComments] || ''}
                            onChange={(e) => handleChange(`documentComments.${doc.key}`, e.target.value)}
                            placeholder="Add comments..."
                            style={{ width: '100%', border: 'none', background: 'transparent', padding: '4px' }}
                          />
                        </td>
                        <td style={{ border: '1px solid #d1d5db', padding: '8px' }}>
                          <input
                            type="file"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              // Update both the files and the document flag
                              handleChange(`uploadedDocuments.${doc.key as keyof UploadedDocuments}`, files);
                              // Only set the document flag for the main document type
                              // Handle identity card front/back specially
                              if (doc.key === 'identityCardFront' || doc.key === 'identityCardBack') {
                                const hasIdFront = doc.key === 'identityCardFront' ? files.length > 0 : !!(formData.uploadedDocuments?.identityCardFront || []).length;
                                const hasIdBack = doc.key === 'identityCardBack' ? files.length > 0 : !!(formData.uploadedDocuments?.identityCardBack || []).length;
                                handleChange('documents.identityCard', hasIdFront && hasIdBack);
                              } else if (doc.key === 'passport' || doc.key === 'utilityBill' || doc.key === 'bankReference' || doc.key === 'cv' || doc.key === 'trustDeed' || doc.key === 'boardMinutes' || doc.key === 'powerOfAttorney' || doc.key === 'other') {
                                handleChange(`documents.${doc.key}`, files.length > 0);
                              }
                            }}
                            style={{ fontSize: '12px' }}
                          />
                          {(() => {
                            const files = getUploadedFiles(formData.uploadedDocuments, doc.key as keyof UploadedDocuments);
                            return files.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>
                                {files.length} file(s) uploaded
                            </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* SUMSUB ID Verification */}
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>SUMSUB ID Verification</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                Complete your identity verification using our secure SUMSUB integration.
              </p>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <input
                  type="checkbox"
                  checked={formData.sumsubVerificationCompleted}
                  onChange={(e) => handleChange('sumsubVerificationCompleted', e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px', color: '#1f2937' }}>
                  I have completed the SUMSUB ID verification process
                </span>
              </div>
              
              <button
                type="button"
                onClick={startKYCProcess}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '6px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#3b82f6';
                }}
              >
                Start SUMSUB Verification
              </button>
            </div>
          </div>
        );
      case 11:
        return renderReviewSignStep();
      default:
        return null;
    }
  };

  return (
    <div className="register-container">
      <div className="register-sidebar">
        <div>
          <div style={{ fontSize: branding.sidebar.logo.fontSize, fontWeight: branding.sidebar.logo.fontWeight, marginBottom: branding.sidebar.logo.marginBottom, color: branding.sidebar.logo.color }}>
            {branding.sidebar.logoText}
          </div>
          <h1 style={{ fontSize: branding.sidebar.title.fontSize, fontWeight: branding.sidebar.title.fontWeight, marginBottom: branding.sidebar.title.marginBottom, color: branding.sidebar.title.color }}>
            Enhanced Personal Registration
          </h1>
          <p style={{ fontSize: branding.sidebar.text.fontSize, lineHeight: branding.sidebar.text.lineHeight, color: branding.sidebar.text.color }}>
            A streamlined flow based on the KYC Individuals form, with digital signature and PDF download.
          </p>
        </div>
        <div style={{ fontSize: branding.sidebar.footer.fontSize, color: branding.sidebar.footer.color }}>
          © {new Date().getFullYear()} {branding.sidebar.logoText}. All rights reserved.
        </div>
      </div>
      <div className="business-register-main">
        {/* Classic Horizontal Stepper - All Steps Visible */}
        <div style={{ 
          marginBottom: '48px', 
          padding: '20px 0',
          width: '100%'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start',
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            padding: '0 10px'
          }}>
            {steps.map((label, index) => (
              <div key={label} style={{ 
                display: 'flex', 
                alignItems: 'center',
                flexShrink: 0,
                minWidth: '80px'
              }}>
                {/* Step Circle */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  minWidth: '80px'
                }}>
                  <div style={{ 
                    width: '28px', 
                    height: '28px', 
                    borderRadius: '50%', 
                    backgroundColor: index === activeStep ? '#3b82f6' : index < activeStep ? '#10b981' : '#e5e7eb',
                    color: index === activeStep ? 'white' : index < activeStep ? 'white' : '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    border: index === activeStep ? '2px solid #3b82f6' : index < activeStep ? '2px solid #10b981' : '2px solid #e5e7eb',
                    transition: 'all 0.3s ease'
                  }}>
                    {index < activeStep ? '✓' : index + 1}
                  </div>
                  <div style={{ 
                    marginTop: '6px',
                    fontSize: '10px',
                    color: index === activeStep ? '#3b82f6' : index < activeStep ? '#10b981' : '#6b7280',
                    fontWeight: index === activeStep ? '600' : '500',
                    textAlign: 'center',
                    maxWidth: '80px',
                    lineHeight: '1.1',
                    wordWrap: 'break-word'
                  }}>
                    {label}
                  </div>
                </div>
                
                {/* Connecting Line */}
                {index < steps.length - 1 && (
                  <div style={{ 
                    width: '40px', 
                    height: '2px', 
                    backgroundColor: index < activeStep ? '#10b981' : '#e5e7eb',
                    margin: '0 5px',
                    transition: 'all 0.3s ease'
                  }} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="form-container">
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 600, color: '#111827', margin: 0 }}>
                {steps[activeStep]}
              </h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                {/* TEMPORARILY HIDDEN - Fill Mock Data button 
                <button
                  type="button"
                  onClick={fillWithMockData}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    borderRadius: '6px',
                    background: '#dbeafe',
                    color: '#1e40af',
                    border: '1px solid #93c5fd',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#bfdbfe';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#dbeafe';
                  }}
                >
                  Fill Mock Data
                </button>
                */}
                <button
                  type="button"
                  onClick={clearAllData}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    borderRadius: '6px',
                    background: '#fef2f2',
                    color: '#dc2626',
                    border: '1px solid #fecaca',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#fee2e2';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#fef2f2';
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              Step {activeStep + 1} of {steps.length}
            </p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={(e) => e.preventDefault()}>
            {renderStep()}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '40px', width: '100%' }}>
              {activeStep > 0 && (
                <button 
                  type="button" 
                  onClick={handleBack} 
                  disabled={loading}
                  style={{
                    height: branding.forms.button.height,
                    padding: branding.forms.button.padding,
                    fontSize: branding.forms.button.fontSize,
                    fontWeight: branding.forms.button.fontWeight,
                    borderRadius: branding.forms.button.borderRadius,
                    background: branding.forms.button.secondary.background,
                    color: branding.forms.button.secondary.color,
                    border: `1px solid ${branding.forms.button.secondary.border}`,
                    cursor: 'pointer',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  Back
                </button>
              )}
              <button 
                type="button" 
                onClick={handleNext} 
                disabled={!isStepValid || loading}
                style={{
                  height: branding.forms.button.height,
                  padding: branding.forms.button.padding,
                  fontSize: branding.forms.button.fontSize,
                  fontWeight: branding.forms.button.fontWeight,
                  borderRadius: branding.forms.button.borderRadius,
                  background: branding.forms.button.primary.background,
                  color: branding.forms.button.primary.color,
                  border: 'none',
                  cursor: 'pointer',
                  opacity: (!isStepValid || loading) ? 0.6 : 1
                }}
              >
                {activeStep === steps.length - 1 ? (loading ? 'Generating...' : 'Complete Registration') : 'Continue'}
              </button>
            </div>
          </form>

          <div style={{ textAlign: 'center', marginTop: '32px', color: branding.forms.label.color, fontSize: branding.forms.input.fontSize }}>
            Prefer the classic flow? 
            <Link to="/register/personal" style={{ color: branding.primaryColor, textDecoration: 'none', fontWeight: branding.forms.button.fontWeight, marginLeft: '4px' }}>
              Use original registration
            </Link>
          </div>
        </div>
      </div>

      {showKycModal && kycApplicantId && kycAccessToken && (
        <SumsubKYC
          applicantId={kycApplicantId}
          accessToken={kycAccessToken}
          onComplete={handleKYCComplete}
          onError={handleKYCError}
          onClose={handleKYCClose}
        />
      )}
    </div>
  );
};

export default EnhancedPersonalRegister;


